// app-context.js
import React, { createContext, useContext, useEffect, useRef } from "react";
import { useImmerReducer } from "use-immer";
import firebase, { db, analytics, batch, functionsInstance } from "./firebase.js";
import { httpsCallable } from "@firebase/functions";
import { doc, collection, setDoc, addDoc, deleteDoc, getDoc, updateDoc, getDocs, query, where, onSnapshot, serverTimestamp, writeBatch, increment } from "firebase/firestore";
import { logEvent } from "firebase/analytics";
import { AuthContext } from "./auth-provider.js";
import { matchPath, useLocation, useSearchParams } from "react-router-dom";
import { nanoid } from "nanoid";
import { v4 as uuidv4 } from 'uuid';
import assets from "./assets.js";
import { audioUpload, computeCreditSplit } from "../helpers/index.js";
import { emptyStory, getAudioUrl, getMediaUrl, incrementShotTitle } from "./utility.js";
import hostConfig from "./config.js";
import { computeUserRole } from "./permissions.js";
import toast from "react-hot-toast";
import Messages from "../constants/app.js";
import { current } from "immer";
import { initialState } from "./app-reducer.js";

// import { storyConstructor, albumConstructor } from "../utils/data"

const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
const isMobile = Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i))
const isMobileSize = () => { return Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i) || (window && window.innerWidth <= 820)); }
// AppContext is the high level data store context for the App
const AppContext = createContext(null);

export function AppProvider({ children }) {
    // Check to see if the user is authenticated before fetching data
    const { currentUser, updateUser } = useContext(AuthContext);
    const generateCompose = async (storyRef) => {
        if (!state.stories.generateStory) return;
        const composeId = nanoid(20);
        const compRef = doc(storyRef, 'compose', composeId);
        const cost = state.stories.generateStory.cost;

        // Compute credit split between project and user
        const targetStoryRef = storyRef || state.stories.activeStoryRef;
        const { projectAmount, userAmount, payer } = await computeCreditSplit(
            targetStoryRef, cost, currentUser.uid
        );

        // Deduct from project credits if applicable
        if (projectAmount > 0) {
            const projectBalanceRef = doc(targetStoryRef, 'credits', '0');
            await setDoc(projectBalanceRef, { credits: increment(-projectAmount), updated_at: serverTimestamp() }, { merge: true });
        }
        // Deduct from user credits
        if (userAmount > 0) {
            dispatch({ type: "UPDATE_USER_CREDITS", payload: { credits: -userAmount } });
        }

        const composeData = { ...state.stories.generateStory, payer: payer };
        await setDoc(compRef, composeData, { merge: true }).then(() => {
            dispatch({ type: "GENERATE_STORY", payload: { "generateStory": null } });
            dispatch({ type: "CLEAR_NEXT_STORY_METADATA", payload: {} });
        }).catch((error) => {
            console.log('Error adding new compose for user: ', userRef.id, error)
        })
    }
    // Main reducer for App context
    const reducer = (draft, action) => {
        const storyRef = draft.stories.activeStory.ref
        const shotRef = ("payload" in action && action.payload?.shot_id != null && storyRef) ? doc(storyRef, 'shots', action.payload.shot_id) : (("payload" in action && action.payload?.shot?.id != null && storyRef)) ? doc(storyRef, 'shots', action.payload.shot.id) : null
        const takeRef = ("payload" in action && action.payload?.take_id != null && storyRef) ? doc(storyRef, 'takes', action.payload.take_id) : null

        // const userRef = (action.type.search("USER") > -1) ? await setDoc(doc(db, 'users').doc(currentUser.uid) : 0
        switch (action.type) {
            /*case "TOGGLE_USER_CAN_LISTEN_TO_TAKES":
                draft.app.settings.userCanListenToTakes = !draft.app.settings.userCanListenToTakes;
                return draft;*/


            case "PROCESS_FIRST_TAKE": {
                const { activeTakeList } = action.payload || {};
                if (activeTakeList && activeTakeList.length > 0) {
                    const firstTake = activeTakeList[0];
                    if (firstTake && firstTake.stream_path && !firstTake.clip_dur) {
                        getVideoDuration(firstTake.stream_path)
                            .then((duration) => {
                            })
                            .catch((error) => {
                                console.error("Error processing first take:", error);
                            });
                    }
                }
                return draft;
            }
            case "PROCESS_SELECTED_ITEM": {
                const { selectedItem } = draft;
                if (selectedItem && selectedItem.stream_path) {
                    getVideoDuration(selectedItem.stream_path)
                        .then((duration) => {
                            const shotRef = doc(storyRef, "shots", selectedItem.shot_id);
                            return updateDoc(shotRef, { shot_dur: duration })
                                .then(() => console.log("Shot updated successfully with duration."));
                        })
                        .catch((error) => {
                            console.error("Error processing selected take:", error);
                        });
                }
                return draft;
            }

            case "UPDATE_AUDIO": {
                const audio = action.payload;
                draft.stories.activeStory.audio = (audio && Object.keys(audio).length) > 0 ? audio : {};
                return draft;
            }

            case "UPLOAD_AUDIO": {
                const { file } = action.payload;
                if (!file) return;
                let audioDoc = {};
                audioUpload({ file, state: draft })
                    .then(async (uploadResult) => {
                        if (uploadResult?.audio_path) {
                            const audioDocRef = doc(storyRef, "audio", "0");
                            audioDoc = {
                                audio_path: uploadResult.audio_path,
                                file_name: file.name,
                                uploaded_at: serverTimestamp(),
                                uploader_id: currentUser.uid,
                            };
                            await setDoc(audioDocRef, audioDoc)
                            dispatch({ type: "UPDATE_AUDIO", payload: audioDoc });
                        }
                    })
                    .catch((error) => {
                        console.error("Error during audio upload process:", error);
                        dispatch({ type: "UPDATE_AUDIO", payload: {} });
                    });
                return draft;

            }
            case "REMOVE_AUDIO": {
                getDoc(doc(storyRef, 'audio', '0'))
                    .then(async (doc) => {
                        await deleteDoc(doc.ref);
                    })
                    .catch((error) => {
                        console.error("Error during audio removal:", error);
                    });
                const nullDoc = async () => {
                    const audioDocRef = doc(storyRef, "audio", "0");
                    await setDoc(audioDocRef, {})
                };
                nullDoc();
                draft.stories.activeStory.audio = null;
                return draft;
            }
            case "MIGRATE_AUDIO": {
                draft.app.migratingAudio = true;
                return draft;
            }
            case "END_MIGRATING_AUDIO": {
                draft.stories.activeStory.last_audio = null;
                draft.stories.activeStory.audio_file_name = null;
                draft.app.migratingAudio = null;
                return draft;
            }
            // toggle story star function
            case "TOGGLE_STORY_STAR": {
                //const { currentUser } = useContext(AuthContext);
                if (!currentUser) {
                    console.error("User is not authenticated");
                    return draft;
                }
                draft.app.toggleStoryStar = action.payload
                return draft
            }
            case "UPDATE_PART_TITLE": {
                const { parts, partId, title } = action.payload;
                if (!parts?.length) return draft;
                else {
                    const newParts = parts.map((part) => {
                        if (part.id === partId) {
                            return { ...part, title: title };
                        }
                        return part;
                    });
                    setDoc(storyRef, { "parts": newParts }, { merge: true })
                    draft.stories.activeStory.parts = newParts;
                }
                return draft;
            }
            case "UPDATE_STORY_PARTS":
                //draft.story.parts = action.payload
                draft.stories.activeStory.parts = action.payload
                const updateParts = action.payload;
                setDoc(storyRef, { "parts": updateParts }, { merge: true })
                return draft;
            case "UPDATE_STORY_SHOTS":
                const part_index = action.payload.part_index
                // draft.story.shots = [...draft.story.shots, ...action.payload.shots]
                // draft.story.parts[part_index].order = action.payload.shots.map(shot => shot.id)

                // we have the option to remove previous shots, or tag them as deleted
                // we need to delete if we want the counter to be accurate ...
                draft.stories.activeStory.parts[part_index].order.forEach((shot_id) => {
                    setDoc(storyRef, 'shots', shot_id,
                        { "deleted_at": serverTimestamp() }, { merge: true }
                    )
                })
                if (storyRef) {
                    action.payload.shots.forEach((shot) => {
                        const shotRef = doc(storyRef, 'shots', shot.id);
                        setDoc(shotRef, shot, { merge: true })
                            .then(() => shotRef)
                            .catch((error) => {
                                console.log('Error creating new shot:', shotRef)
                            })
                    })
                    var new_parts = draft.stories.activeStory.parts
                    new_parts[part_index].order = action.payload.shots.map(shot => shot.id)
                    setDoc(storyRef, { "parts": new_parts }, { merge: true })
                }
                return draft;

            case "UPDATE_STORY_TITLE":
                if (storyRef) {
                    setDoc(storyRef, { "title": action.payload.title }, { merge: true })
                }
                // Optimistic local update — mirrors the Firestore write immediately so
                // the UI doesn't wait for the onSnapshot round-trip.
                draft.stories.activeStory.title = action.payload.title;
                if (draft.stories.storyList) {
                    const idx = draft.stories.storyList.findIndex(s => s.portal_id === draft.stories.activeStory.portal_id);
                    if (idx !== -1) draft.stories.storyList[idx].title = action.payload.title;
                }
                return draft

            case "UPDATE_STORY_DESCRIPTION":
                if (storyRef) {
                    setDoc(storyRef, { "description": action.payload.description }, { merge: true })
                }
                // Optimistic local update — mirrors the Firestore write immediately so
                // the UI doesn't oscillate while waiting for the onSnapshot round-trip.
                draft.stories.activeStory.description = action.payload.description;
                return draft


            case "START_STORY_TIMER":
                const timerStart = action.payload.timerStart

                if (storyRef) {

                    setDoc(storyRef, { "timerStart": timerStart }, { merge: true })
                }
                return draft

            case "TOGGLE_STORY_COMPETITION":
                const isCompetition = action.payload.isCompetition
                if (storyRef) {
                    setDoc(storyRef, { "isCompetition": isCompetition, "timerStart": null }, { merge: true })
                }
                return draft

            case "TOGGLE_PREVIEW_VOLUME":
                const previewVolume = action.payload.volume

                draft.app.previewVolume = previewVolume

                return draft
            case "TOGGLE_MUSIC_VOLUME":
                const musicVolume = action.payload.volume

                draft.app.musicVolume = musicVolume

                return draft
            case "ADD_STORY_SHOT":
                const { part_id, index, shot, auto_title } = action.payload;

                // Find the index of the part where the shot needs to be added
                const part_ind = draft.stories.activeStory.parts.findIndex(part => part.id === part_id);

                if (part_ind === -1) {
                    console.error("Part ID not found");
                    return draft; // Exit early if part_id is invalid
                }

                const parts = JSON.parse(JSON.stringify(draft.stories.activeStory.parts))
                const originalOrder = draft.stories.activeStory.parts[part_ind].order || [];

                // Update the order array in the specified part
                if (typeof index === "number") {
                    parts[part_ind].order = [
                        ...parts[part_ind].order.slice(0, index),
                        shot.id,
                        ...parts[part_ind].order.slice(index)
                    ];
                } else {
                    if (parts[part_ind].order.length === 0) parts[part_ind].order = [shot.id];
                    else parts[part_ind].order = [...parts[part_ind].order, shot.id];
                }

                if (auto_title) {
                    if (typeof index === "number" && index > 0 && originalOrder.length > 0) {
                        const prevShotId = originalOrder[index - 1];
                        const nextShotId = originalOrder[index];
                        const prevShot = draft.stories.activeShotList?.find(s => s.id === prevShotId);
                        const nextShot = draft.stories.activeShotList?.find(s => s.id === nextShotId);
                        shot.header = incrementShotTitle(prevShot?.header, nextShot?.header);
                    } else {
                        shot.header = "Shot " + parts[part_ind].order.length;
                    }
                }

                draft.stories.activeStory.parts = parts;
                // Write the new shot to Firestore and update the part order
                if (shot?.id) {
                    try {
                        const batch = writeBatch(db);

                        // Add the new shot to the "shots" collection
                        const shotRef = doc(storyRef, "shots", shot.id);
                        batch.set(shotRef, shot, { merge: true });

                        // Update the "parts" field in the story
                        batch.set(
                            storyRef,
                            { "parts": parts },
                            { merge: true }
                        );

                        // Commit the batch
                        batch.commit();
                        console.log("Shot added and parts updated successfully.");
                    } catch (error) {
                        console.error("Error creating or updating shot:", error);
                    }
                } else {
                    console.error("shotRef is not defined.");
                }

                return draft; // Immer will finalize the updated draft object

            case "MOVE_STORY_SHOT":
                const source_part_id = action.payload.source_part_id
                const dest_part_id = action.payload.dest_part_id
                const moving_shot_id = action.payload.moving_shot_id
                const preceding_shot_id = action.payload.preceding_shot_id

                var new_parts = draft.stories.activeStory.parts

                const source_part_index = new_parts.map(part => part.id).indexOf(source_part_id)
                if (source_part_index === -1) {
                    console.error("MOVE_STORY_SHOT non-existant part!", source_part_id)
                }

                const dest_part_index = new_parts.map(part => part.id).indexOf(dest_part_id)
                if (dest_part_index === -1) {
                    console.error("MOVE_STORY_SHOT non-existant part!", dest_part_index)
                }

                const dest_shot_index = preceding_shot_id === "000" ? 0 : new_parts[dest_part_index].order.indexOf(preceding_shot_id) + 1

                // first we remove the moving shot
                new_parts[source_part_index].order = new_parts[source_part_index].order.filter(id => id !== moving_shot_id)

                // then we add its id to the destination part
                // new_parts[dest_part_index].order = [...new_parts[dest_part_index].order, moving_shot_id] // at the end
                new_parts[dest_part_index].order.splice(dest_shot_index, 0, moving_shot_id) // where it was dropped

                setDoc(storyRef, { "parts": new_parts }, { merge: true })

                return draft;
            case "DELETE_STORY_SHOT":
                if (!storyRef || !shotRef) {
                    console.error("Missing storyRef for shot deletion");
                    return draft;
                }
                //const shot_ref = doc(storyRef, 'shots', action.payload.shot_id)
                // Create a copy of parts before starting async operation
                const partsBeforeDelete = JSON.parse(JSON.stringify(draft.stories.activeStory.parts));

                // Update local state immediately
                draft.stories.activeStory.parts = partsBeforeDelete.map(part => ({
                    ...part,
                    order: part.order.filter(id => id !== action.payload.shot_id)
                }));

                // Handle database updates
                (async () => {
                    try {
                        const delBatch = writeBatch(db);

                        // Mark shot as deleted
                        delBatch.set(shotRef, {
                            "deleted_at": serverTimestamp()
                        }, { merge: true });

                        // Update story parts
                        delBatch.set(storyRef, {
                            "parts": partsBeforeDelete.map(part => ({
                                ...part,
                                order: part.order.filter(id => id !== action.payload.shot_id)
                            }))
                        }, { merge: true });

                        // Commit the batch
                        await delBatch.commit();
                    } catch (error) {
                        console.error("Error deleting shot:", error);
                        // Revert local state on error
                        draft.stories.activeStory.parts = partsBeforeDelete;
                    }
                })();

                return draft;

            case "HIDE_STORY_SHOT":
                if (!storyRef) {
                    console.error("Missing storyRef or shotRef for shot hide");
                    return draft;
                }
                //const shotRef = doc(storyRef, 'shots', action.payload.shot.id)

                // Handle database updates
                setDoc(shotRef, { hidden: action.payload.shot.hidden ? false : true }, { merge: true });
                draft.stories.activeShotList = draft.stories.activeShotList.map(shot => shot.id === action.payload.shot_id ? { ...shot, hidden: action.payload.hide } : shot);

                return draft;

            case "HIDE_STORY_PART":
                if (!storyRef) {
                    console.error("Missing storyRef for part hide");
                    return draft;
                }
                // Handle database updates
                const newParts = draft.stories.activeStory.parts.map(part => part.id === action.payload.part_id ? { ...part, hidden: action.payload.hide } : part)
                setDoc(storyRef, { parts: newParts }, { merge: true });
                draft.stories.activeStory.parts = newParts;
                return draft;


            case "DELETE_STORYBOARD":
                const { boardRef } = action.payload;
                //const delete_storyboard_batch = boardRef.set();
                //const storyboardRef = await setDoc(doc(db, 'portals').where('story_id', '==', portal_id)

                setDoc(boardRef, { "deleted_at": serverTimestamp() }, { merge: true })

                return draft;





            case "DELETE_TAKE":
                //takeRef.update({"deleted_at": firebase.firestore.FieldValue.serverTimestamp()})
                //const delete_take_batch = db.batch()
                setDoc(takeRef, { "deleted_at": serverTimestamp() }, { merge: true })
                var shot_payload = {}
                if (action.payload.shot_selected_take_id == action.payload.take_id) {
                    shot_payload["selected_take_id"] = null
                    shot_payload["selected_take_stream_url"] = null
                    shot_payload["selected_take_im"] = null
                }
                if (action.payload.shot_last_take_id == action.payload.take_id) {
                    shot_payload["last_take_stream_url"] = null
                    shot_payload["last_take_im"] = null
                    shot_payload["last_take_id"] = null
                }
                if (Object.keys(shot_payload).length > 0) {
                    setDoc(shotRef, shot_payload, { merge: true })
                }

                //delete_take_batch.commit()

                return draft

            case "TRIM_TAKE":
                //takeRef.update({"deleted_at": firebase.firestore.FieldValue.serverTimestamp()})
                //const delete_take_batch = db.batch()
                setDoc(takeRef, { offset_ms: action.payload.offset_ms }, { merge: true })

                return draft

            case "UPDATE_STORY_SHOT_DESCRIPTION":
                // function replaceDescIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {shot.description = payload.description}
                // }
                // draft.story.shots.map(shot => replaceDescIfIdMatches(shot, action.payload))
                //const sid = action.payload.shot_id
                const desc = action.payload.description
                //let sref = /*shotRef !== undefined ? shotRef : */doc(storyRef, 'shots', sid);
                if (!shotRef) return;
                const updateShotDesc = async (sref, description) => {
                    await setDoc(sref, { "description": description }, { merge: true })
                }
                updateShotDesc(shotRef, desc);

                return draft

            case "UPDATE_STORY_SHOT_TITLE":
                // function replaceTitleIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {shot.header = payload.text}
                // }
                // draft.story.shots.map(shot => replaceTitleIfIdMatches(shot,action.payload))
                const { shot_id, text } = action.payload;
                let ref = /*shotRef !== undefined ? shotRef : */doc(storyRef, 'shots', shot_id);
                if (!ref) return;
                const updateShotTitle = async (ref, title) => {
                    await setDoc(ref, { "header": title }, { merge: true })
                }
                updateShotTitle(ref, text);

                return draft

            case "UPDATE_STORY_STYLE":
                const style = action.payload.style;
                draft.stories.activeStory.storyStyle = style;
                setDoc(storyRef, { "storyStyle": style }, { merge: true })
                return draft

            case "UPDATE_STORY_MODE":
                const mode = action.payload.mode;
                const vertical = (mode === "vertical") ? true : false
                draft.stories.activeStory.is_vertical = vertical;
                setDoc(storyRef, { "is_vertical": vertical }, { merge: true })
                return draft

            case "CREATE_VENEER_FOR_TAKE":
                draft.app.createVeneer = action.payload
                return draft

            case "CLEAR_CREATE_VENEER":
                draft.app.createVeneer = null
                return draft

            /* OLD STORY SHOT TITLE UPDATE
            case "UPDATE_STORY_SHOT_TITLE":
                const { shot_id, text } = action.payload;
                if (shot_id && text !== undefined) {
                    draft.story.shots.forEach(shot => {
                        if (shot.id === shot_id) {
                            shot.header = text;
                        }
                    });
            
                    setDoc(shotRef, { header: text }, {merge:true})
                }
                //         .then(() => {
                //             // console.log("Document successfully updated!");
                //         })
                //         .catch(error => {
                //             // console.error("Error updating document: ", error);
                //         });
                // } else {
                //     // console.error("Invalid shot_id or text:", shot_id, text);
                // }
            
                return draft
            
             */



            case "UPDATE_STORY_SHOT_PROMPT":
                setDoc(shotRef, { "prompt": action.payload.prompt, "prompt_composition": action.payload.composition, "prompt_style": action.payload.style }, { merge: true })
                return draft

            case "UPDATE_USER_CREDITS":
                if (!currentUser) return draft;
                draft.app.updateCredits = action.payload.credits;
                if (action.payload.credits && action.payload.credits !== 0) {
                    draft.userData.credits = draft.userData.credits + action.payload.credits;
                }
                return draft
            case "CLEAR_LAST_CREDITS":
                if (draft.userData.ref) {
                    draft.userData.lastCredits = null;
                }
                return draft
            case "UPDATE_USER_CREDIT_BALANCE":
                // Only merge credit fields if userData has been initialized (has ref)
                // to avoid populating userData prematurely which breaks init flow checks
                if (draft.userData.ref) {
                    draft.userData.credits = action.payload.credits;
                    draft.userData.processingCredits = action.payload.processingCredits;
                    draft.userData.lastCredits = action.payload.lastCredits;
                }
                return draft

            case "UPDATE_STORY_SHOT_IMAGE":
                // function replaceImageIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {shot.image = payload.image}
                // }
                // draft.story.shots.map(shot => replaceImageIfIdMatches(shot,action.payload))
                setDoc(shotRef, { "image": action.payload.image }, { merge: true })

                return draft
            /*case "UPDATE_SHOT_SELECTED_TAKE_IM": // possibly depricate in favor of UPDATE_SHOT_SELECTED_TAKE_IM
                setDoc(shotRef, {"last_take_im": action.payload.image}) // this was working

                return draft
            case "UPDATE_SHOT_EMPTY_TAKE":
                    setDoc(shotRef, {"selected_take_id": null, "last_take_id": null, "last_take_im": null, "last_take_stream_url": null, "image": action.payload.image}) // proposed new
                    // setDoc(shotRef, {"selected_take_im": action.payload.image})  maybe we add this logic ... selected_take_id
    
                    return draft
            */

            case "UPDATE_SHOT_LAST_TAKE":
                //const sRef = doc(storyRef, 'shots', action.payload.shot_id)
                setDoc(shotRef, { "last_take_id": action.payload.take_id, "last_take_im": action.payload.image, "last_take_stream_url": action.payload.stream_path }, { merge: true }) // proposed new
                    .catch((error) => {
                        console.log('Error updating shot last take:', shotRef)
                    })
                return draft

            case "UPDATE_SHOT_SELECTED_TAKE":
                //const shotRef = doc(storyRef, 'shots', action.payload.shot_id)
                setDoc(shotRef, { "selected_take_id": (action.payload.image) ? action.payload.take_id : null, "selected_take_im": action.payload.image, "selected_take_stream_url": action.payload.stream_path }, { merge: true }) // proposed new
                // setDoc(shotRef, {"selected_take_im": action.payload.image})  maybe we add this logic ... selected_take_id
                return draft

            case "FORK_STORY":
                draft.app.forkingStory = action.payload
                return draft;

            case "CLEAR_FORK":
                draft.app.forkingStory = null;
                return draft;

            case "CHECK_MOBILE":
                draft.app.isMobileSize = isMobileSize();
                return draft;

            case "UPDATE_PORTAL_PASS":
                if (!action.payload.portal?.id) return draft;
                const portalRef = doc(db, 'portals', action.payload.portal.id)
                console.log("ACTION", action.payload)
                setDoc(portalRef, { "requirePass": action.payload.requirePass }, { merge: true }).then(() => {
                    dispatch({ type: "UPDATE_PORTAL", payload: { ...action.payload.portal, "requirePass": action.payload.requirePass } });
                    console.log("B")
                })
                    .catch((error) => {
                        console.log('Error updating portal pass:', action.payload.portal, action.payload.requirePass, error)
                    })
                return draft;

            case "STORY_IMPORT":
                draft.app.storyImport = action.payload


                return draft;

            case "ADD_TAKE_TO_SHOT":
                // push take to the cloud under SHOT
                const payload = action.payload;
                //const activeStoryRef = draft.stories.activeStoryRef   // this gets cleared
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const uploader_email = (currentUser?.email) ? currentUser.email : (payload.uploader_email || null)
                    const take = {
                        video_path: payload.video_path,
                        thumbnail: payload.thumbnail,
                        id: payload.take_id,
                        shot_id: payload.shot_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        uploader_email: uploader_email,
                        proxyLUT: draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        deleted_at: null,
                        source: payload.source,
                        is_ref: payload.is_ref || false,
                        is_vertical: payload.is_vertical || false,
                    };


                    //dur_ms
                    //est_created_time - from filename? or
                    //  // payload.user_id  .. why are these undefined!?  vs e.g. ahbMAd47J1aWnEhDrq5PwjQSmBU2


                    // I get the sense that this may be the way to go for large projects, with GET instead of snapshot
                    // but having N listeners for each participant seems overkill. 
                    //    this code remains here so it could be reanble for testing at a future date
                    //    As of Sept 2021 - Mar 2022 (including okapi-webdev data), this was mirrored in both places.
                    //    This state is not properly maintained for subsequent changes, like thumbnail updates, etc.
                    //  In Mar 2022, including all of 10zebra data, this was depricated.
                    //
                    // const takeRefALT = storyRef.collection('shots').doc(payload.shot_id).collection('takes').doc(payload.take_id)
                    // takeRefALT.set(take)
                    //    .then(() => takeRefALT)
                    // .catch((error) => {
                    //   console.log('Error creating new take:', takeRefALT)
                    // })

                    // we are actually using these takes for rendering the active state in the app
                    const takeRef = doc(storyRef, 'takes', payload.take_id)
                    setDoc(takeRef, take, { merge: true })
                        .then(() => {
                            if (take.generate) {
                                setDoc(takeRef, { generate: false }, { merge: true })
                                dispatch({ type: "START_VENEER", payload: {} });
                            }
                            return takeRef
                        })
                        .catch((error) => {
                            console.log('Error creating new take:', takeRef)
                        })
                    //console.log("ADD_TAKE_TO_SHOT  cloud ", payload.take_id)

                } else {
                    console.log("WARNING! Cant add take to story", draft.stories.activeStory)
                }

                // function addTakeIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {
                //         shot.takes = shot.takes || [];
                //         shot.takes.push({video_path: payload.video_path, thumbnail: payload.thumbnail})
                //     }
                // }
                // draft.story.shots.map(shot => addTakeIfIdMatches(shot,action.payload))
                return draft

            case "ADD_IMAGE_TO_SHOT":
                // push image to the cloud, we will treat it as a video with 1 second log, plus some meta data
                const im_payload = action.payload;
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const uploader_email = (currentUser?.email) ? currentUser.email : (im_payload.uploader_email || null)
                    const take = {
                        image_path: im_payload.image_path,
                        // video_path: payload.video_path,  // should we add an loading video until we replace it?
                        thumbnail: im_payload.thumbnail,
                        id: im_payload.take_id,
                        shot_id: im_payload.shot_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        uploader_email: uploader_email,
                        proxyLUT: draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        is_image: true,
                        is_ref: im_payload.is_ref || false,
                        is_vertical: im_payload.is_vertical || false,
                        source: im_payload.source || "upload",
                        deleted_at: null
                    };

                    const takeRef = doc(storyRef, 'takes', im_payload.take_id)
                    setDoc(takeRef, take, { merge: true })
                        .then(() => takeRef)
                        .catch((error) => {
                            console.log('Error creating new take:', takeRef)
                        })
                } else {
                    console.log("WARNING! Cant add image take to story", draft.stories.activeStory)
                }

                return draft

            /*case "ADD_TAKE_AS_REF":
                // push image to the cloud, we will treat it as a video with 1 second log, plus some meta data
                const ref_payload = action.payload;
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const take = {image_path: ref_payload.image_path, 
                         video_path: ref_payload.video_path,  // should we add an loading video until we replace it?
                        thumbnail: ref_payload.thumbnail,
                        id: ref_payload.take_id,
                        shot_id: ref_payload.shot_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        proxyLUT : draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        is_image: true,
                        is_ref: true,
                        source: ref_payload.source || "upload", 
                        deleted_at: null}; 
                    
                const takeRef = doc(storyRef, 'takes', ref_payload.take_id)
                setDoc(takeRef, take, {merge:true})
                    .then(() => takeRef)
                .catch((error) => {
                    console.log('Error creating new take:', takeRef)
                })
                } else {
                    console.log("WARNING! Cant add image take to story", draft.stories.activeStory)
                }

                return draft
            */
            case "ADD_TAKE_REF":
                draft.app.newRefTake = action.payload;
                return draft;

            case "CLEAR_TAKE_REF":
                draft.app.newRefTake = null;
                return draft;

            case "UPDATE_TAKE":
                if (!storyRef) return;
                const update_payload = action.payload;
                const updateRef = doc(storyRef, 'takes', update_payload.id)
                setDoc(updateRef, update_payload, { merge: true })
                    //.then(() => updateRef)
                    .catch((error) => {
                        console.log('Error updating take:', updateRef)
                    })
                return draft;

            case "ADD_TMP_REF":
                draft.app.tmpRef = action.payload;
                return draft;

            case "CLEAR_TMP_REF":
                draft.app.tmpRef = null;
                return draft;

            case "TOGGLE_SHOT_REF": {
                const toggleId = action.payload.shot_id;
                const currentShotRefs = draft.stories.activeStory.shot_refs || [];
                const shotRefExists = currentShotRefs.includes(toggleId);
                const updatedShotRefs = shotRefExists
                    ? currentShotRefs.filter(r => r !== toggleId)
                    : [...currentShotRefs, toggleId];
                draft.stories.activeStory.shot_refs = updatedShotRefs;
                if (storyRef) setDoc(storyRef, { shot_refs: updatedShotRefs }, { merge: true });
                return draft;
            }

            case "CLEAR_USER_REFS":
                draft.generate.userRefs = null;
                // draft.generate.userRefs = [];
                return draft;

            case "CLEAR_USER_STATE":
                draft.app.updateUserState = null;
                return draft;

            case "UPDATE_USER_STATE":
                // DEPRECATED: use persistUserState() from helpers/index.js instead.
                // This case remains temporarily to catch any missed callers during migration.
                console.warn("[DEPRECATED] UPDATE_USER_STATE dispatched — migrate caller to persistUserState()", action.payload);
                draft.app.updateUserState = action.payload;
                return draft;

            case "UPDATE_USER_REFS":
                draft.generate.userRefs = action.payload;
                // draft.stories.activeStory.currentRefs = action.payload;
                return draft;

            case "UPDATE_SELECTED_METHOD":
                draft.generate.method = action.payload;
                return draft;

            case "UPDATE_SELECTED_OUTPUT":
                draft.generate.output = action.payload;
                return draft;

            case "UPDATE_SELECTED_MODEL":
                draft.generate.model = action.payload;
                return draft;

            case "UPDATE_SYSTEM_MODEL":
                // System-driven model override (e.g. bumped for ref capacity).
                // Never written to Firebase — cleared when user explicitly selects a model.
                draft.generate.systemModel = action.payload;
                return draft;

            case "UPDATE_PROMPT":
                draft.generate.prompt = action.payload;
                return draft;

            case "ADD_IMAGE_TO_TMP":
                // push image to the cloud, we will treat it as a video with 1 second log, plus some meta data
                const img_payload = action.payload;
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const uploader_email = (currentUser?.email) ? currentUser.email : (img_payload.uploader_email || null)
                    const take = {
                        image_path: img_payload.image_path,
                        // video_path: payload.video_path,  // should we add an loading video until we replace it?
                        thumbnail: img_payload.thumbnail,
                        id: img_payload.take_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        uploader_email: uploader_email,
                        proxyLUT: draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        deleted_at: null
                    };

                    const takeRef = doc(storyRef, 'takes', img_payload.take_id)
                    setDoc(takeRef, take, { merge: true })
                        .then(() => takeRef)
                        .catch((error) => {
                            console.log('Error creating new take:', takeRef)
                        })
                } else {
                    console.log("WARNING! Cant add image take to story", draft.stories.activeStory)
                }

                return draft;

            case "MOVE_TAKE_TO_SHOT":

                const destShotRef = ("payload" in action && "dest_shot_id" in action.payload) ? doc(storyRef, 'shots', action.payload.dest_shot_id) : null
                //const oldShotRef = ("payload" in action && "shot_id" in action.payload) ? storyRef.collection('shots').doc(action.payload.shot_id) : null

                setDoc(takeRef, { "shot_id": action.payload.dest_shot_id, "modified_at": serverTimestamp() }, { merge: true })

                setDoc(destShotRef, { "last_take_id": action.payload.take_id, "last_take_im": action.payload.image, "last_take_stream_url": action.payload.stream_path }, { merge: true })
                // if dest shot has selected shot
                var shot_payload = {}
                if (action.payload.prev_selected_take_id == action.payload.take_id) {
                    shot_payload["selected_take_id"] = null
                    shot_payload["selected_take_stream_url"] = null
                    shot_payload["selected_take_im"] = null
                }
                if (action.payload.prev_last_take_id == action.payload.take_id) {
                    shot_payload["last_take_stream_url"] = null
                    shot_payload["last_take_im"] = null
                    shot_payload["last_take_id"] = null
                }
                if (Object.keys(shot_payload).length > 0) {
                    setDoc(shotRef, shot_payload, { merge: true })
                }

                return draft
            // case "UPDATE_NUM_SHOTS":
            //     draft.story.num_shots = action.payload.num_shots;
            //     return draft;
            case "RESET_STATE":
                draft = initialState;
                return draft;

            case "DO_USER_INIT":
                doUserInit();
                return;

            case "SAVE_CUT":
                draft.app.savingCut = action.payload;

                return draft;

            case "UPDATE_GENERATE_FETCHED":
                if (action.payload === false) {
                    // Resetting to false means a new story is loading — wipe all
                    // stale generate state so the previous story's prompt/method/refs/model
                    // don't bleed through before the new story's Firebase fetch completes.
                    draft.generate = {
                        method: "text2im",
                        model: null,
                        systemModel: null,
                        userRefs: [],
                        prompt: null,
                        fetched: false,
                    };
                } else {
                    draft.generate.fetched = true;
                }
                return draft;

            case "SET_PROMPT":
                draft.generate.prompt = action.payload.prompt;
                return draft;

            case "CLEAR_LAST_CUT":
                draft.stories.activeStory.last_cut = null
                // storyRef.update{}
                return draft;
            case "CLEAR_SAVE_CUT":
                draft.app.savingCut = null
                // storyRef.update{}
                return draft;
            case "CLEAR_LAST_EXPORT":
                draft.stories.activeStory.last_export = null
                return draft;
            case "CLEAR_LAST_ACTIVE":
                draft.stories.setLastActive = null;
                return draft;
            case "APP_LOADED":
                draft.app.isLoading = false;
                return draft;
            case "MOVE_TAKE":
                draft.app.movingTake = action.payload.moving;
                return draft;
            case "ADD_ALERT": // Add alert to the list of active alerts
                // type: success, info, warning, error
                // message: string
                draft.app.alerts[Date.now()] = action.payload.alert;
                return draft;
            case "CLEAR_ALERT":
                delete draft.app.alerts[action.payload.key];
                return draft;
            case "MODAL":  // Set open/closed draft of App dialogs
                draft.app.modal = action.payload;
                return draft;
            case "CONTROL":  // Top level draft of app controls
                draft.app.controls[action.payload.name] = action.payload.state;
                return draft;
            case "RESET_FLIP":  // Reset flip initiator
                draft.app.controls.flip = false;
                return draft;
            case "FLIP_SHOTS":  // Initiate card flip sequence
                draft.app.controls.flip = true;
                return draft;
            case "SET_PREVIEW_IMAGE":  // Insert the preview into draft.cardPreviews with the cardID as the key
                // args: cardID, src
                {
                    let preview = {
                        src: action.payload.src,
                        orientation: action.payload.orientation,
                    }
                    draft.app.cardPreviews[action.payload.cardID] = preview;
                    return draft;
                }
            case "CLEAR_PREVIEW_IMAGE":  // Delete the image keyed off the cardID
                // args: cardID
                delete draft.app.cardPreviews[action.payload.cardID]
                return draft;
            case "START_UPLOAD":  // Configure and open upload modal
                logEvent(analytics, 'START_UPLOAD');
                const upload = {
                    data: {
                        totalFiles: action.payload.totalFiles,
                        totalBytes: action.payload.totalBytes,
                        uploadCount: 0,
                        failedCount: 0,
                        failedNames: [],
                        fileBytes: {},
                    }
                }
                draft.app.upload = upload;
                return draft;
            case "FILE_UPLOAD_UPDATE": {
                if (draft.app.upload) {
                    draft.app.upload.data.fileBytes[action.payload.cardID] = action.payload.bytes;
                }
                return draft;
            }
            case "FILE_UPLOAD_SUCCESS":  // Register one successful file uploaded
                if (draft.app.upload) {
                    draft.app.upload.data.uploadCount++;
                    draft.app.contributedCount = draft.app.contributedCount + draft.app.upload.data.totalFiles;
                    const story = draft.stories.activeStory;
                    logEvent(analytics, 'FILE_UPLOAD_SUCCESS'); // , { count: draft.app.contributedCount});
                    // TODO: Clean up cards for failed uploads and notify the user
                    // Even if they have a preview image, they need an original image to be valid
                    if (draft.app.upload.data.uploadCount + draft.app.upload.data.failedCount === draft.app.upload.data.totalFiles) {
                        draft.app.upload = null
                    }
                    /*if (story.isCompetition && (story.timerStart == undefined || story.timerStart == null)) {
                        const timerStart = Date.now();
                        const payload = { timerStart: timerStart };
                        if(dispatch) dispatch({ type: "START_STORY_TIMER", payload: payload });
                    }*/
                }
                return draft;
            case "FILE_UPLOAD_FAILURE":  // Register a failed file upload
                // args: fileName
                if (draft.app.upload) {
                    draft.app.alerts[Date.now()] = {
                        type: 'error',
                        message: `Upload failed for ${action.payload.fileName}`,
                    }
                    draft.app.upload.data.failedCount++;
                    draft.app.upload.data.failedNames.push(action.payload.fileName)
                    logEvent(analytics, 'FILE_UPLOAD_FAILURE') //, { filename: action.payload.fileName}); 

                    // Even if they have a preview image, they need an original image to be valid
                    if (draft.app.upload.data.uploadCount + draft.app.upload.data.failedCount === draft.app.upload.data.totalFiles) {
                        // TODO: Open file failure modal to show names and let the user confirm
                        draft.app.upload = null
                    }
                } else {
                    console.error('Attempt to register file upload failure but file upload modal is not open.')
                }
                return draft;
            case "START_IMPORT":
                logEvent(analytics, 'START_IMPORT');
                const importing = {  // could update to frames processed ... not really required. 
                    data: {
                        totalFrames: 100,
                        processedFrames: 10,
                    }
                }
                draft.app.importing = importing;
                return draft;
            case "IMPORT_SUCCESS":
                // this just ends alert for now
                if (draft.app.importing) {
                    draft.app.importing = null
                }
                return draft;
            case "UPDATE_PORTAL":
                draft.app.portal = action.payload;
                return draft;
            case "UPDATE_ACTIVE_SHOT":
                //draft.stories.activeShotID = action.payload.shot_id;
                return draft;
            case "ADD_REQUESTED_PORTAL":
                draft.app.portal.requested_id = action.payload.portal_id;
                return draft;
            case "CLEAR_REQUESTED_PORTAL":
                draft.app.portal.requested_id = null;
                return draft;
            case "UPDATE_ACTIVE_STORY_REF":
                draft.stories.setLastActive = (action.payload.setLastActive) ? action.payload.setLastActive : null;
                draft.stories.activeStoryRef = action.payload.activeStoryRef;
                // Only clear story data when genuinely navigating to a different story.
                // If the story doc ID matches what is already loaded, preserve state
                // so the UI never flashes "Collaborative Video" unnecessarily.
                if (action.payload.activeStoryRef?.id !== draft.stories.activeStory?.ref?.id) {
                    draft.stories.activeStory = {};
                    draft.stories.activeShotList = [];
                    draft.stories.activeTakeList = [];
                    draft.stories.activeRefList = [];
                    draft.generate.method = null;
                    draft.generate.model = null;
                    draft.generate.systemModel = null;
                    draft.generate.prompt = null;
                    draft.generate.userRefs = [];
                    draft.generate.fetched = false;
                    draft.app.isLoading = true;
                    // Reset role fields so they are never stale from the previous story.
                    draft.app.userIsStoryOwner = false;
                    draft.app.userProjectRole = null;
                }
                return draft;
            case "SET_LAST_ACTIVE":
                draft.stories.lastActiveStory = action.payload.lastActiveStory;
                return draft;
            case "CLEAR_ACTIVE_STORY_REF":
                draft.stories.activeStoryRef = null;
                draft.stories.activeStory = {}
                draft.stories.activeShotList = [];
                draft.stories.activeTakeList = [];
                draft.stories.activeRefList = [];
                draft.stories.activeStoryCredits = null;
                draft.generate = {
                    method: null,
                    model: null,
                    userRefs: [],
                    prompt: null,
                    fetched: false
                }
                return draft;
            case "CLEAR_ACTIVE_ALBUM_REF":
                draft.inspiration.activeAlbumRef = null;
                draft.inspiration.activeAlbum = {}
                draft.inspiration.activeAlbumCardList = [];
                return draft;
            case "UPDATE_USER_DATA":
                draft.userData = action.payload.userData;
                return draft;
            case "GET_USER_DATA":
                draft.userData.getUserData = action.payload.getUserData;
                return draft;
            case "UPDATE_STORY_LIST":
                draft.stories.storyList = action.payload.storyList;
                return draft;
            case "UPDATE_SHARED_STORY_LIST":
                draft.stories.sharedStoryList = action.payload.sharedStoryList;
                return draft;
            case "NEW_ACTIVE_STORY":
                draft.stories.nextStoryMetadata = action.payload.newStory
                //draft.stories.activeStory = action.payload.newStory;
                return draft;
            case "CLEAR_NEXT_STORY_METADATA":
                draft.stories.nextStoryMetadata = null
                return draft;
            case "UPDATE_ACTIVE_STORY":
                //draft.app.userIsStoryOwner = false
                draft.stories.activeStory = action.payload.activeStory;
                return draft;
            case "UPDATE_ACTIVE_STORY_CREDITS":
                draft.stories.activeStoryCredits = action.payload.credits;
                return draft;
            case 'SET_USER_IS_STORY_OWNER':
                draft.app.userIsStoryOwner = action.payload.userIsStoryOwner;
                return draft;
            case 'SET_USER_PROJECT_ROLE':
                draft.app.userProjectRole = action.payload.userProjectRole;
                return draft;
            case 'ADD_PROJECT_MEMBER': {
                const portal = draft.app.portal;
                if (!portal) return draft;
                const members = portal.members || [];
                members.push(action.payload.member);
                portal.members = members;
                if (action.payload.member.status === "accepted" && action.payload.member.uid) {
                    const uids = portal.member_uids || [];
                    if (!uids.includes(action.payload.member.uid)) {
                        uids.push(action.payload.member.uid);
                    }
                    portal.member_uids = uids;
                }
                // Signal Cloud Function call via updateMembers useEffect
                draft.app.updateMembers = {
                    portal_id: portal.id,
                    action: "add",
                    member: JSON.parse(JSON.stringify(action.payload.member)),
                };
                return draft;
            }
            case 'REMOVE_PROJECT_MEMBER': {
                const rmPortal = draft.app.portal;
                if (!rmPortal) return draft;
                rmPortal.members = (rmPortal.members || []).filter(
                    m => !(m.uid === action.payload.uid || m.invite_code === action.payload.invite_code)
                );
                rmPortal.member_uids = (rmPortal.member_uids || []).filter(uid => uid !== action.payload.uid);
                draft.app.updateMembers = {
                    portal_id: rmPortal.id,
                    action: "remove",
                    remove_uid: action.payload.uid,
                    remove_invite_code: action.payload.invite_code,
                };
                return draft;
            }
            case 'ACCEPT_PROJECT_INVITE': {
                const invPortal = draft.app.portal;
                if (!invPortal || draft.app.userIsStoryOwner) return draft;
                const invMembers = invPortal.members || [];
                const invMatch = invMembers.find(m => m.invite_code === action.payload.invite_code);
                if (invMatch && invMatch.status === "pending") {
                    invMatch.uid = action.payload.uid;
                    invMatch.status = "accepted";
                    const invUids = invPortal.member_uids || [];
                    if (!invUids.includes(action.payload.uid)) {
                        invUids.push(action.payload.uid);
                    }
                    invPortal.member_uids = invUids;
                    draft.app.updateMembers = {
                        portal_id: invPortal.id,
                        action: "accept_invite",
                        invite_code: action.payload.invite_code,
                    };
                }
                draft.app.userProjectRole = "member";
                return draft;
            }
            case 'CLEAR_UPDATE_MEMBERS':
                draft.app.updateMembers = null;
                return draft;
            case "UPDATE_ACTIVE_SHOT_LIST":
                draft.stories.activeShotList = action.payload.activeShotList;
                return draft;
            case "UPDATE_ACTIVE_REF_LIST":
                draft.stories.activeRefList = action.payload?.activeRefList || (Array.isArray(action.payload) ? action.payload : []);
                return draft;
            case "UPDATE_ACTIVE_TAKE_LIST":
                draft.stories.activeTakeList = action.payload.activeTakeList;
                return draft;
            case "UPDATE_ACTIVE_VENEER_LUT":
                const VeneerLUT = {};
                action.payload.activeVeneerList.forEach((vn) => {
                    VeneerLUT[vn.ref.id] = vn
                })
                draft.stories.activeVeneerLUT = VeneerLUT
                return draft;
            case "UPDATE_STORY_SETTINGS":
                let new_settings = draft.app.settings
                for (const key in action.payload.settings) {
                    new_settings[key] = action.payload.settings[key]
                }
                draft.app.settings = new_settings
                draft.app.newSettings = action.payload.new || false
                return draft
            case "CLEAR_NEW_SETTINGS":
                draft.app.newSettings = false
                return draft
            case "UPDATE_STORY_MODELS":
                const { defaultModels, disallowedModels } = action.payload;
                const models = defaultModels ? { defaultModels: defaultModels } : disallowedModels ? { disallowedModels: disallowedModels } : null;
                if (models) setDoc(storyRef, models, { merge: true });
                return draft;
            case "UPDATE_PRIORITIZE_LIVE_PREVIEW":
                draft.app.prioritizeLivePreview = action.payload.prioritizeLivePreview;
                return draft
            case "UPDATE_ALBUM_SOURCE_SET":
                const newSourceSet = new Set();
                action.payload.sourceSet.forEach((sourceID) => {
                    newSourceSet.add(sourceID)
                })
                draft.stories.albumSourceSet = newSourceSet;
                return draft;
            case "UPDATE_ACTIVE_ALBUM_SHOT_LIST":
                draft.inspiration.activeAlbumCardList = action.payload.activeAlbumCardList;
                return draft;
            case "UPDATE_ALBUM_LIST":
                draft.inspiration.albumList = action.payload.albumList;
                return draft;
            case "UPDATE_ACTIVE_ALBUM_REF":
                draft.inspiration.activeAlbumRef = action.payload.activeAlbumRef;
                return draft;
            case "UPDATE_ACTIVE_ALBUM":
                draft.inspiration.activeAlbum = action.payload.activeAlbum;
                return draft;
            case "SET_STARRED_STORIES":
                draft.stories.starredStories = action.payload.starredStories;
                return draft;
            case "GENERATE_STORY":
                draft.stories.generateStory = action.payload.generateStory;
                return draft;
            case "FORK_FROM_PORTAL":
                draft.app.initAndFork = { portalId: action.payload.portalId, fork: action.payload.fork }
                return draft;
            case "CLEAR_INIT_AND_FORK":
                draft.app.initAndFork = null;
                return draft;
            case "TRANSFER_CREDITS_TO_PROJECT":
                draft.app.projectCreditTransfer = {
                    direction: 'toProject',
                    amount: action.payload.amount,
                    storyRef: action.payload.storyRef,
                };
                return draft;
            case "TRANSFER_CREDITS_FROM_PROJECT":
                draft.app.projectCreditTransfer = {
                    direction: 'fromProject',
                    amount: action.payload.amount,
                    storyRef: action.payload.storyRef,
                };
                return draft;
            case "CLEAR_PROJECT_CREDIT_TRANSFER":
                draft.app.projectCreditTransfer = null;
                return draft;
            default:
                return draft;
        }
    };
    const [state, dispatch] = useImmerReducer(reducer, initialState);
    const routerLocation = useLocation();
    const intervalRef = useRef(null);
    const intervalCountRef = useRef(0);
    const portalCacheRef = useRef(null);
    const importsCountRef = useRef(0);
    // Prevents concurrent/duplicate initializeFromPortal calls for the same portal
    const storyInitLockRef = useRef(null);

    // RESET STATE
    // Fires only on genuine user changes:
    //   1. User logs out (currentUser → null) → full reset
    //   2. User switches accounts (uid mismatch) → reset + re-init
    // Does NOT fire while userData is loading (userData.id=undefined, currentUser present)
    // — the portal/user init effects handle that window naturally.
    useEffect(() => {
        if (!currentUser) {
            // Logged out — reset to initial state
            console.log('[INIT DEBUG] RESET_STATE: user logged out');
            dispatch({ type: "RESET_STATE" });
            return;
        }
        if (state.userData.id && state.userData.id !== currentUser.uid) {
            // Account switch — reset and re-init for new user
            console.log('[INIT DEBUG] RESET_STATE: account switch', state.userData.id, '→', currentUser.uid);
            dispatch({ type: "RESET_STATE" });
            doUserInit();
        }
        // userData.id is undefined but currentUser exists = still loading, do nothing
    }, [dispatch, currentUser, state.userData?.id]);

    // Real-time listener for credit data on /users/{uid}/credits/0 subcollection
    // Isolated from user doc writes (e.g. lastActiveStory) to avoid unnecessary triggers
    useEffect(() => {
        if (!currentUser) return;
        const balanceRef = doc(db, 'users', currentUser.uid, 'credits', '0');
        const userRef = doc(db, 'users', currentUser.uid);
        const unsubscribe = onSnapshot(balanceRef, async (snapshot) => {
            if (!snapshot.exists()) {
                // Self-healing: backfill /credits/0 from legacy user doc fields for existing users
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const legacy = userSnap.data();
                    if (legacy.credits !== undefined) {
                        await setDoc(balanceRef, {
                            credits: legacy.credits || 0,
                            processingCredits: legacy.processingCredits || false,
                            lastCredits: legacy.lastCredits || null,
                        });
                        console.log('Self-healed: migrated credits to /credits/0');
                    }
                }
                return;
            }
            const balanceData = snapshot.data();

            // Merge only credit fields into userData — avoid replacing the whole object
            // which would wipe userData.ref due to stale closure over state
            console.log('[INIT DEBUG] Credit listener fired. userData.ref exists=', !!state.userData?.ref, 'userData keys=', Object.keys(state.userData));
            dispatch({
                type: "UPDATE_USER_CREDIT_BALANCE", payload: {
                    credits: balanceData.credits,
                    processingCredits: balanceData.processingCredits,
                    lastCredits: balanceData.lastCredits,
                }
            });

            // Clear lastCredits once credit processing is complete
            if (balanceData.lastCredits && balanceData.lastCredits != balanceData.credits && balanceData.processingCredits == false) {
                dispatch({ type: "CLEAR_LAST_CREDITS", payload: {} });
            }
        }, (error) => {
            console.log('Error listening to credit data: ', error);
        });
        return () => unsubscribe();
    }, [currentUser]);

    const getUserData = async (uDoc) => {
        let user, newDoc = uDoc;
        if (!currentUser) { return }
        //let unSubscribe = () => { };
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            const balanceRef = doc(db, 'users', currentUser.uid, 'credits', '0');
            //let doc = uDoc;
            if (!newDoc || !newDoc.exists()) {
                await getDoc(userRef).then((uDoc) => {// db.database().ref('portal/' + portal_id + '/story_path');
                    newDoc = uDoc;
                })
            }
            if (!newDoc || !newDoc.exists()) return;
            let userData = newDoc.data();

            // Read processingCredits from /credits/0 subcollection
            const balanceSnap = await getDoc(balanceRef);
            const balanceData = balanceSnap.exists() ? balanceSnap.data() : {};
            const isProcessing = balanceData.processingCredits == true;

            if (isProcessing) {
                console.log("PROCESSING CREDITS - WAITING", intervalCountRef.current);
                if (!intervalRef.current) intervalRef.current = setInterval(getUserData, 1000);
                else if (intervalCountRef.current >= 5) {
                    clearInterval(intervalRef.current)
                    intervalRef.current = null;
                    intervalCountRef.current = 0;
                    dispatch({ type: "GET_USER_DATA", payload: { getUserData: false } })
                    await setDoc(balanceRef, { processingCredits: false, lastCredits: null }, { merge: true });
                    console.log("PROCESSING ERROR: Clearing processingCredits after 5 tries")
                }
                else intervalCountRef.current = intervalCountRef.current + 1;

                return;
            } else {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    intervalCountRef.current = 0;
                }
            }
            if (!userData.email) return;
            userData.id = currentUser.uid;  // ensure id is always set for downstream effects
            userData.ref = newDoc;
            userData.superuser = false;
            userData.zebradmin = false;
            userData.photoURL = currentUser.photoURL;
            // Merge credit data from subcollection
            userData.credits = balanceData.credits || userData.credits;
            userData.processingCredits = balanceData.processingCredits || false;
            userData.lastCredits = balanceData.lastCredits || null;
            if (userData.email === 'pmm3123@gmail.com') {
                userData.superuser = true;
            }
            if (!userData.account) {
                // old accounts are set to free and 100 credits
                userData.account = "free";
            }
            if (userData.email.indexOf("@10zebra.com") != -1 || userData.email.indexOf("@apollofractional.com") != -1) {
                userData.zebradmin = true;
                if (userData.credits < 50) {
                    userData.credits = 500;
                }
            }
            dispatch({ type: "UPDATE_USER_DATA", payload: { userData: userData } });
            console.log('[INIT DEBUG] getUserData complete. userData.id=', userData.id, 'userData.ref=', !!userData.ref, 'credits=', userData.credits);
            return userData;
        } catch (error) {
            console.log('Error getting user data: ', error)
        } finally {
            console.log("user init complete finally")
            //return () => unSubscribe();
        }
    }

    const getPortal = async (portal_id) => {
        try {
            const portalRef = doc(db, 'portals', portal_id);
            let portal = {};
            await getDoc(portalRef).then((pRef) => {
                if (pRef.exists()) {
                    const data = pRef.data();
                    portal = { ...data, id: portal_id };
                }
            })
            portalCacheRef.current = portal;
            return portal;
        } catch (error) {
            console.log('Error getting portal data: ', error);
            return {};
        }
    }

    const checkPortalPass = async (portal_id, returnPortal) => {
        let passed = false;
        let portal = {};
        const checkPass = (pass) => {
            if (pass === "" || pass === null || pass === undefined) return true;
            const passwords = JSON.parse(sessionStorage.getItem('passwords') || '[]');
            const found = passwords.find(p => p.id === portal_id && p.password === pass);
            return found !== undefined;
        }
        if (state.app.portal?.id && portal_id === state.app.portal.id) {
            passed = checkPass(state.app.portal.requirePass);
        } else if (portalCacheRef.current?.id === portal_id) {
            portal = portalCacheRef.current;
            passed = checkPass(portal.requirePass);
        } else {
            portal = await getPortal(portal_id);
            dispatch({ type: "UPDATE_PORTAL", payload: portal });
            passed = checkPass(portal.requirePass);
        }
        return returnPortal ? { portal: portal, passed: passed } : passed;
    }


    // define some async functions that are used in multiple Effects: initial user setup and new project creation
    const initializeFromPortal = async (portal_id, fork, setActive, cachedPortalData) => {
        const userRef = currentUser ? doc(db, 'users', currentUser.uid) : null;
        try {
            // Fix 2: Use cached portal data if available, avoiding duplicate Firestore fetch
            let portal = cachedPortalData || null;
            if (!portal) {
                const portalRef = doc(db, 'portals', portal_id);
                const pRef = await getDoc(portalRef);
                portal = pRef.exists() ? pRef.data() : null;
            }
            if (!portal) return null;
            const storyRef = doc(db, 'users', portal.creator_id, 'stories', portal.story_id);
            if (!storyRef) {
                return null;
            }
            if (setActive) {
                // Fix 1: Fetch story doc and settings doc in parallel
                const settingsRef = doc(storyRef, 'settings', '0');
                const [sDoc, settings] = await Promise.all([
                    getDoc(storyRef),
                    getDoc(settingsRef)
                ]);
                if (sDoc.exists() && !sDoc.data().deleted_at) {
                    const activeStoryRef = sDoc.ref;
                    if (settings.exists()) {
                        dispatch({ type: "UPDATE_STORY_SETTINGS", payload: { settings: settings.data() } });
                    }
                    dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: currentUser?.uid == portal.creator_id } });
                    dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { 'activeStoryRef': activeStoryRef, setLastActive: state.stories.setLastActive || false } });
                    currentUser?.uid == portal.creator_id ? logEvent(analytics, 'OWNER_PORTAL_LOAD') : logEvent(analytics, 'COMMUNITY_PORTAL_LOAD');
                } else {
                    console.log("Story? No such document!");
                    return null;
                }
            } else {
                // Non-setActive paths (fork, read-only) still need the story doc
                const sDoc = await getDoc(storyRef);
                if (sDoc.exists() && !sDoc.data().deleted_at) {
                    const data = sDoc.data();
                    if (setActive) {
                        const settingsRef = doc(storyRef, 'settings', '0');
                        const settings = await getDoc(settingsRef);
                        if (settings.exists()) {
                            dispatch({ type: "UPDATE_STORY_SETTINGS", payload: { settings: settings.data() } });
                        }
                        dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: currentUser?.uid == portal.creator_id } });
                        dispatch({ type: "SET_USER_PROJECT_ROLE", payload: { userProjectRole: computeUserRole(currentUser, portal) } });
                        dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { 'activeStoryRef': activeStoryRef } });
                        currentUser?.uid == portal.creator_id ? logEvent(analytics, 'OWNER_PORTAL_LOAD') : logEvent(analytics, 'COMMUNITY_PORTAL_LOAD');
                    }
                    else if (fork) {
                        let forkObj = structuredClone(fork);
                        forkObj.parts = data.parts;
                        forkObj.description = data.description || null;
                        if ("audio" in data) {
                            forkObj["audio"] = data.audio;
                        }
                        forkObj["last_cut"] = data.last_cut;
                        if (userRef) {
                            state.app.userIsStoryOwner ? logEvent(analytics, 'OWNER_FORK') : logEvent(analytics, 'COMMUNITY_FORK');
                            dispatch({ type: "FORK_STORY", payload: { forkingStory: forkObj, mediaOption: "All Media", userRef: userRef, storyRef: storyRef, newStory: forkObj } });
                            dispatch({ type: "CLEAR_FORK", payload: {} })
                        }
                        return null;
                    } else {
                        let story = data;
                        story.ref = sDoc.ref;
                        return story;
                    }
                } else {
                    console.log("Story? No such document!");
                    return null;
                }
            }
            return null;
        } catch (error) {
            console.log('Error querying data for portal defined Story: ', error);
            return null;
        }
    };

    useEffect(() => {
        const run = async () => {
            if (!currentUser) return;
            if (!state.stories.storyList) return;
            if (state.stories.starredStories.length) return;
            const portals = await getDocs(collection(db, 'users', currentUser.uid, 'starred_portals'));
            const docs = (portals) ? portals.docs : [];
            let starredBoards = [];
            if (docs.length) {
                const promises = docs.map(async (doc) => {
                    if (doc.exists()) {
                        const data = doc.data();
                        if (!data) return;
                        if (state.stories.storyList && state.stories.storyList.find(story => story.portal_id === data.portal_id) !== undefined) {
                            starredBoards.push(state.stories.storyList.find(story => story.portal_id === data.portal_id));
                        } else {
                            const resp = await initializeFromPortal(data.portal_id);
                            if (resp) starredBoards.push(resp) // = [...starredBoards, ...[resp]];
                        }
                    }
                });
                await Promise.all(promises);
            }
            dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories: starredBoards } });
        };
        run();
    }, [currentUser, state.stories.storyList]); // currentUser, 

    const setupInitialStory = async (userRef) => {
        const storyRef = doc(userRef, 'stories');
        const newStory = initialStory; // storyConstructor();

        newStory.shots.forEach((shot, key) => {
            const shotRef = doc(storyRef, 'shots', shot.id);
            setDoc(shotRef, shot, { merge: true })
                .then(() => shotRef)
                .catch((error) => {
                    console.log('Error creating new shot:', shotRef)
                })
        })

        return setDoc(storyRef, newStory, { merge: true })
            .then(() => storyRef)
            .catch((error) => {
                console.log('Error creating new story for user:', userRef.id)
                return error
            })
    }

    const setupNewUser = async (user) => {
        const storyId = nanoid(20);
        const name = user.displayName || "";
        const newUser = {
            name: user.displayName,
            email: user.email,
            account: "free",
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
            deleted_at: null,
        }
        const addUser = async () => {
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, newUser, { merge: true })
            // Initialize credits in /credits/0 subcollection
            const balanceRef = doc(userRef, 'credits', '0');
            await setDoc(balanceRef, {
                credits: 250,
                processingCredits: false,
                lastCredits: null,
            });
            const userDoc = await getDoc(userRef)
            if (userDoc.exists()) {
                let data = userDoc.data();
                data.ref = userDoc;
                data.credits = 250;
                dispatch({ type: "UPDATE_USER_DATA", payload: { userData: data } });
            }
            logEvent(analytics, "NEW_USER: " + user.email);
        }
        addUser();
    }

    const doUserInit = async () => {
        // Purely fetches user data and dispatches it.
        // Story initialization is handled exclusively by the dedicated story-load effect below.
        if (!currentUser) { return }
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            await getDoc(userRef).then(async (uDoc) => {
                if (uDoc.exists()) {
                    await getUserData(uDoc);

                    // Read lastActiveStory from /state/0 subcollection, with fallback to user doc
                    const userData = uDoc.data();
                    const stateDocRef = doc(userRef, 'state', '0');
                    const stateSnap = await getDoc(stateDocRef);
                    const rawLastActive = stateSnap.exists()
                        ? stateSnap.data().lastActiveStory
                        : userData.lastActiveStory; // fallback to legacy user doc field

                    // Normalize: handle legacy string path or new { storyId, portalId } object
                    let lastActiveStory = null;
                    if (rawLastActive && typeof rawLastActive === 'object' && rawLastActive.storyId) {
                        lastActiveStory = rawLastActive;
                    } else if (typeof rawLastActive === 'string') {
                        // Legacy: extract storyId from path "users/xyz/stories/abc"
                        const arr = rawLastActive.split("/");
                        lastActiveStory = { storyId: arr.pop(), portalId: null };
                    }

                    // Pre-populate reducer so storyboards view can use portalId immediately
                    if (lastActiveStory) {
                        dispatch({ type: "SET_LAST_ACTIVE", payload: { lastActiveStory } });
                    }

                    // Handle post-auth redirects / callbacks
                    if (currentUser.redirect) {
                        navigate(currentUser.redirect);
                        currentUser.redirect = null;
                    }
                    if (currentUser.authFunc) {
                        currentUser.authFunc();
                        currentUser.authFunc = null;
                    }
                } else {
                    setupNewUser(currentUser)
                }
            })
        } catch (error) {
            console.log('Error in doUserInit: ', error)
        } finally {
            console.log('[INIT DEBUG] doUserInit complete')
        }
    }

    const initializeActiveStoryRef = async (userRef, storyRef/*, portal*/) => {
        let activeStoryRef = storyRef //doc(db, storyRef);
        /*if (storyRef) {
            // Check if story exists 
            await getDoc(doc(db, storyRef)).then(sDoc => {
                if(sDoc.exists()) activeStoryRef = sDoc
            });
        }*/
        // If not, check if there are other stories to set it to and set it to the first one
        if (!storyRef) {
            // The original activeStory no longer exists, so check fo any other stories
            const querySnapshot = await getDocs(query(collection(userRef, 'stories'), orderBy("date", "desc"), limit(1)));
            activeStoryRef = (querySnapshot.exists()) ? querySnapshot.docs[0].ref : null;
        }

        // If no stories, create one and set it
        if (!activeStoryRef) {
            await setupInitialStory(userRef)
                .then((newStoryRef) => activeStoryRef = newStoryRef)
                .catch((error) => {
                    console.log('Unable to initialize activeStoryRef. Creating new story failed.', error)
                })
        }
        if (activeStoryRef) {
            dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { activeStoryRef: activeStoryRef, setLastActive: true } });
            /*await setDoc(userRef, { "lastActiveStory": activeStoryRef.path }, {merge:true})
                .catch((error) => {
                    console.error('Error writing activeStoryRef to user document in database: ', error)
                })*/
        }
    }

    const setStoryOwner = () => {
        let userIsStoryOwner = false;

        const storyPathArray = state.stories.activeStoryRef.path.split("/");

        const userIndex = storyPathArray.findIndex((item) => item === "users") + 1;
        if (storyPathArray[userIndex] && currentUser?.uid === storyPathArray[userIndex]) {
            if (!state.app.userIsStoryOwner) dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: true } });
        } else {
            if (state.app.userIsStoryOwner) dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: false } });
        }
    }

    // USER DATA
    // Listen for updates to userData and initialize active story
    // If no user data, set up new user and new story
    // PORTAL & USER INIT COORDINATOR
    // Step 1: Parse portal_id from URL and fetch the portal doc if not yet in state.
    //         Dispatching UPDATE_PORTAL re-triggers this effect with portal.id populated.
    // Step 2: Once portal.id is set but user data is absent, call doUserInit.
    //         doUserInit dispatches UPDATE_USER_DATA which populates userData.id,
    //         which re-triggers the dedicated story-load effect below.
    // Story initialization is NOT done here — it lives in its own effect.
    useEffect(() => {
        const loc = window.location;
        let path = matchPath({ path: '/p/:id/:shotID', exact: false, string: false }, loc.pathname)
        if (!path) {
            path = matchPath({ path: '/p/:id', exact: false, string: false }, loc.pathname)
        }
        const onPortalRoute = path != null;
        const urlPortalId = path?.params?.id;

        // --- User data init ---
        // If logged in and user data not yet loaded, fetch it immediately regardless of route.
        if (currentUser && !Object.keys(state.userData).length) {
            console.log('[INIT DEBUG] Calling doUserInit');
            doUserInit();
        }

        // --- Portal doc init ---
        if (!state.app.portal.id) {
            if (onPortalRoute && urlPortalId && urlPortalId !== 'undefined') {
                console.log('[INIT DEBUG] Step 1 – fetching portal:', urlPortalId);
                const handleGetPortal = async () => {
                    const portal = await getPortal(urlPortalId); // sets portalCacheRef.current
                    dispatch({ type: "UPDATE_PORTAL", payload: portal });
                };
                handleGetPortal();
            }
            return;
        }

        // --- Fully initialized: handle cross-portal navigation ---
        // If the URL points to a different portal than the current one, dispatch UPDATE_PORTAL
        // so the story-load effect (below) fires for the new portal.
        if (onPortalRoute && urlPortalId && urlPortalId !== state.app.portal.id) {
            // Always fetch the new portal so state.app.portal (creator_id, members) is
            // up to date for role computation, regardless of how the navigation was triggered.
            // storyboards.js dispatches UPDATE_ACTIVE_STORY_REF which starts the snapshot
            // listener — we just need the portal to resolve roles correctly.
            console.log('[INIT DEBUG] Cross-portal navigation to:', urlPortalId);
            const handleGetPortal = async () => {
                const portal = await getPortal(urlPortalId);
                dispatch({ type: "UPDATE_PORTAL", payload: portal });
            };
            handleGetPortal();
        }

    }, [dispatch, currentUser, state.app.portal?.id, state.userData?.email, routerLocation.pathname])

    // DEDICATED STORY-LOAD EFFECT
    // Single owner of initializeFromPortal for the setActive=true path.
    // Fires when portal.id is set AND:
    //   - Logged-in users: userData.email is also confirmed (user data fully loaded)
    //   - Anonymous users: currentUser is null, portal.id is enough to proceed
    // storyInitLockRef prevents duplicate calls when deps change in quick succession.
    useEffect(() => {
        if (!state.app.portal?.id) return;
        // Logged-in users must wait for user data before loading (ensures correct ownership/roles)
        if (currentUser && !state.userData?.email) return;
        const portal_id = state.app.portal.id;

        // Prevent concurrent duplicate calls for the same portal
        if (storyInitLockRef.current === portal_id) return;

        // If the correct story is already loaded (ref doc ID matches portal's story_id),
        // the active snapshot is live and up-to-date — no re-init needed.
        // This covers navigating back to a story from storyboards without unnecessary resets.
        if (state.stories.activeStoryRef?.id === state.app.portal?.story_id &&
            state.stories.activeStoryRef?.id != null) return;

        storyInitLockRef.current = portal_id;
        const load = async () => {
            try {
                const cached = portalCacheRef.current?.id === portal_id ? portalCacheRef.current : null;
                await initializeFromPortal(portal_id, null, true, cached);
            } finally {
                storyInitLockRef.current = null;
            }
        };
        load();
    }, [currentUser?.uid, state.app.portal?.id, state.userData?.email])


    // Make a new story out of next story
    // Next story is locally in the app.   It propagates out to the cloud and back. 
    useEffect(() => {
        if (!state.stories.nextStoryMetadata) return;
        if (state.stories.generateStory && state.userData.credits - state.stories.generateStory.cost < 0) return;
        const newStory = { ...state.stories.nextStoryMetadata, ...{ is_vertical: state.stories.generateStory?.is_vertical || false } };
        const userRef = doc(db, 'users', currentUser.uid);
        const newStoryId = nanoid(20);

        const createStory = async () => {
            const newStoryRef = doc(userRef, 'stories', newStoryId);

            // Immediately clear stale story data and point to the new story ref —
            // this mirrors setActiveStory() in storyboards.js and gives an instant
            // clean slate (no flash of old shots/prompt) before Firestore commits.
            dispatch({ type: "CLEAR_ACTIVE_STORY_REF" });
            dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { activeStoryRef: newStoryRef, setLastActive: true } });

            await setDoc(doc(userRef, 'stories', newStoryId), newStory, { merge: true });
            const newShotRef = collection(userRef, 'stories', newStoryId, 'shots')
            if (newStory.shots.length) {
                newStory.shots.forEach(async (shot, key) => {
                    await setDoc(doc(newShotRef, shot.id), shot, { merge: true })
                })
            }
            // setDoc(userRef, {"lastActiveStory": newStoryRef.path}, {merge:true});
            setDoc(newStoryRef, newStory, { merge: true })
            const portalRef = doc(db, 'portals', newStory.portal_id);
            const newPortal = {
                creator_id: currentUser.uid,
                story_id: newStoryId,
                created_at: serverTimestamp(),
                deleted_at: null,
                requirePass: null
            }
            setDoc(portalRef, newPortal, { merge: true })
                .then(() => {
                    logEvent(analytics, 'NEW_STORY')
                    // Eagerly populate portal state so the produce view's role check resolves
                    // to "owner" without waiting for initializeFromPortal to re-fetch from Firestore.
                    // (The portal doc was just committed above, so this is accurate.)
                    dispatch({ type: "UPDATE_PORTAL", payload: { ...newPortal, id: newStory.portal_id } });
                    dispatch({ type: "CLEAR_NEXT_STORY_METADATA" });
                    if (state.stories.generateStory) generateCompose(newStoryRef)
                }).catch((error) => {
                    console.error('Error creating new story: ', error)
                })
        }
        createStory();
    }, [state.stories.nextStoryMetadata])

    // Toggle Star
    useEffect(() => {
        const run = async () => {
            if (!currentUser || !state.app.toggleStoryStar) return;
            const userRef = doc(db, 'users', currentUser.uid);
            const { isStarred, portal_id } = state.app.toggleStoryStar;
            const starredPortalRef = doc(userRef, 'starred_portals', portal_id);
            if (!isStarred) {
                await setDoc(starredPortalRef, { portal_id: portal_id }, { merge: true })
                    .then(async () => {
                        // const starred = { ...state.stories.starredStories, ...{ portal_id: portal_id }}
                        // dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories:starredBoards } });
                        let data;
                        if (state.stories.storyList && state.stories.storyList.find(story => story.portal_id === portal_id)) {
                            data = state.stories.storyList.find(story => story.portal_id === portal_id);
                        } else {
                            data = await initializeFromPortal(portal_id);
                        }
                        const starredBoards = [...state.stories.starredStories, ...[data]]
                        dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories: starredBoards } });
                    })
                    .catch((error) => console.error("Error starring portal: ", error));
            } else {
                await deleteDoc(starredPortalRef).then(() => {
                    const starred = state.stories.starredStories.filter(board => board.portal_id !== portal_id); // Remove the story from the state
                    dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories: starred } });
                })
                    .catch((error) => console.error("Error unstarring portal: ", error));
                // need to add starred dispatch - removed item
            }
        };
        run();
    }, [state.app.toggleStoryStar])

    // Fork Story
    useEffect(() => {
        if (!state.app.forkingStory) return;
        const forkIt = async () => {
            const { forkingStory, mediaOption, userRef, newStory, storyRef } = state.app.forkingStory;
            const storyId = nanoid();

            // Helper to ensure we have a genuine Firestore DocumentReference
            const ensureDocRef = (rawRef, fallbackPath) => {
                if (!rawRef && !fallbackPath) return null;
                // If it's a DocumentSnapshot with .ref, extract the DocumentReference
                let target = (rawRef && rawRef.ref && typeof rawRef.ref === 'object' && rawRef.ref.path) ? rawRef.ref : rawRef;
                // If it has a path string, re-wrap it cleanly via doc(db, path)
                if (target && target.path && typeof target.path === 'string') {
                    return doc(db, target.path);
                }
                if (fallbackPath) {
                    return doc(db, fallbackPath);
                }
                return null;
            };

            const userUid = state.userData?.id || currentUser?.uid;
            let resolvedUserRef = ensureDocRef(userRef, userUid ? `users/${userUid}` : null);

            let resolvedStoryRef = ensureDocRef(storyRef);
            if (!resolvedStoryRef) {
                resolvedStoryRef = ensureDocRef(state.stories.activeStoryRef) || ensureDocRef(state.stories.activeStory?.ref);
            }
            if (!resolvedStoryRef && state.app.portal?.creator_id && state.app.portal?.story_id) {
                resolvedStoryRef = doc(db, 'users', state.app.portal.creator_id, 'stories', state.app.portal.story_id);
            }

            if (!resolvedUserRef || !resolvedStoryRef) {
                console.error("FORK ERROR: missing userRef or storyRef", { resolvedUserRef, resolvedStoryRef });
                toast.error(Messages.error.projectForkFailed);
                dispatch({ type: "CLEAR_FORK" });
                return;
            }

            let story, shots, takes;
            if (forkingStory) {
                story = forkingStory;
                const shotSnapshot = await getDocs(query(collection(resolvedStoryRef, 'shots'), where('deleted_at', '==', null)));
                const activeShotList = [];
                shotSnapshot.forEach((doc) => {
                    if (doc.exists()) {
                        var shot = doc.data();
                        shot.ref = doc.ref;
                        shot.id = shot.id || doc.id;
                        activeShotList.push(shot);
                    }
                });
                shots = activeShotList;
                const takeSnapshot = await getDocs(query(collection(resolvedStoryRef, 'takes'), where('deleted_at', '==', null)));
                const activeTakeList = [];
                takeSnapshot.forEach(snapDoc => {
                    if (snapDoc.exists()) {
                        var take = snapDoc.data();
                        take.ref = snapDoc.ref;
                        take.id = take.id || snapDoc.id;
                        activeTakeList.push(take);
                    }
                });
                takes = activeTakeList;
            } else {
                story = state.stories.activeStory;
                // If activeStory is missing or lacks parts, load directly from Firestore
                if (!story || !story.parts || !story.parts.length) {
                    try {
                        const sDoc = await getDoc(resolvedStoryRef);
                        if (sDoc.exists()) {
                            story = { ref: sDoc.ref, ...sDoc.data() };
                        }
                    } catch (e) {
                        console.error("Error fetching story document for fork: ", e);
                    }
                }
                shots = (state.stories.activeShotList && state.stories.activeShotList.length > 0) ? state.stories.activeShotList : [];
                if (shots.length === 0) {
                    try {
                        const shotSnapshot = await getDocs(query(collection(resolvedStoryRef, 'shots'), where('deleted_at', '==', null)));
                        shotSnapshot.forEach((doc) => {
                            if (doc.exists()) {
                                var s = doc.data();
                                s.ref = doc.ref;
                                s.id = s.id || doc.id;
                                shots.push(s);
                            }
                        });
                    } catch (e) {
                        console.error("Error fetching shots for fork: ", e);
                    }
                }
                takes = (state.stories.activeTakeList || []).concat(state.stories.activeRefList || []);
                if (takes.length === 0 && mediaOption !== "No Media") {
                    try {
                        const takeSnapshot = await getDocs(query(collection(resolvedStoryRef, 'takes'), where('deleted_at', '==', null)));
                        takeSnapshot.forEach(snapDoc => {
                            if (snapDoc.exists()) {
                                var t = snapDoc.data();
                                t.ref = snapDoc.ref;
                                t.id = t.id || snapDoc.id;
                                takes.push(t);
                            }
                        });
                    } catch (e) {
                        console.error("Error fetching takes for fork: ", e);
                    }
                }
            }

            const portalId = newStory.portal_id;
            const portalRef = doc(db, 'portals', portalId);
            const forkBatch = writeBatch(db);
            forkBatch.set(portalRef, {
                creator_id: resolvedUserRef.id,
                story_id: storyId,
                created_at: serverTimestamp(),
            }, { merge: true });

            const forkStory = async () => {
                try {
                    const newStoryRef = doc(resolvedUserRef, 'stories', storyId);
                    const storyPayload = {
                        ...newStory,
                        parts: (story && story.parts && story.parts.length) ? story.parts : (newStory.parts || []),
                    };
                    forkBatch.set(newStoryRef, storyPayload, { merge: true });
                    forkBatch.set(newStoryRef, { 'ref': newStoryRef }, { merge: true });

                    const partsToIterate = storyPayload.parts || [];
                    const shotPromises = partsToIterate.flatMap(part =>
                        (part.order || []).map(async (shot_id) => {
                            if (!shot_id) return;
                            const this_shot = shots.find((shot) => (shot.id || shot.ref?.id) == shot_id);
                            if (!this_shot || this_shot.deleted_at) {
                                console.log(`Shot not copied (deleted or not found) for ID: ${shot_id}`);
                                return;
                            }
                            const newShotRef = doc(newStoryRef, 'shots', shot_id);
                            let image = this_shot.image;
                            let last_take_stream_url = null;

                            if (this_shot.last_take_im) {
                                image = this_shot.last_take_im;
                            }
                            if (this_shot.selected_take_id) {
                                const this_take_list = takes.filter((take) => (take.id || take.ref?.id) == this_shot.selected_take_id);
                                const this_take = this_take_list[0];
                                if (this_take) {
                                    image = this_take.thumbnail;
                                }
                            }

                            let prompt = this_shot.prompt || "";
                            let composition = this_shot.prompt_composition || null;
                            let style = this_shot.prompt_style || null;

                            if (this_shot.selected_take_id) {
                                const this_veneer = state.stories.activeVeneerLUT ? state.stories.activeVeneerLUT[this_shot.selected_take_id] : null;
                                if (this_veneer) {
                                    prompt = this_veneer.prompt;
                                    composition = this_veneer.prompt_composition || composition;
                                    style = this_veneer.prompt_style || style;
                                }
                            }

                            const newShot = {
                                id: shot_id,
                                header: this_shot.header,
                                image: image,
                                meta: "",
                                prompt: prompt,
                                description: this_shot.description,
                                last_take_id: (mediaOption === "No Media") ? null : this_shot.last_take_id || null,
                                selected_take_id: (mediaOption === "No Media") ? null : this_shot.selected_take_id || null,
                                last_take_im: (mediaOption === "No Media") ? null : this_shot.last_take_im || null,
                                last_take_stream_url: (mediaOption === "No Media") ? null : last_take_stream_url,
                                deleted_at: null,
                                prompt_composition: composition,
                                prompt_style: style
                            };
                            if ("tgt_stream_url" in this_shot) {
                                newShot["tgt_stream_url"] = this_shot.tgt_stream_url;
                            }
                            if ("tgt_dur_ms" in this_shot) {
                                newShot["tgt_dur_ms"] = this_shot.tgt_dur_ms;
                            }
                            return setDoc(newShotRef, newShot, { merge: true });
                        })
                    );

                    await Promise.all(shotPromises);
                    // Copy settings
                    try {
                        const settingsDoc = await getDoc(doc(resolvedStoryRef, 'settings', '0'));
                        if (settingsDoc && typeof settingsDoc.exists === 'function' && settingsDoc.exists()) {
                            const settingsData = settingsDoc.data();
                            const newSettingsRef = doc(newStoryRef, 'settings', '0');
                            forkBatch.set(newSettingsRef, settingsData);
                            console.log("Settings copied successfully.");
                        } else {
                            console.log("No settings found to copy.");
                        }
                    } catch (error) {
                        console.error("Error fetching settings: ", error);
                    }
                    // Copy audio
                    try {
                        const audioDoc = await getDoc(doc(resolvedStoryRef, 'audio', '0'));
                        if (audioDoc && typeof audioDoc.exists === 'function' && audioDoc.exists()) {
                            const audioData = audioDoc.data();
                            const newAudioRef = doc(newStoryRef, 'audio', '0');
                            forkBatch.set(newAudioRef, audioData);
                            console.log("Audio copied successfully.");
                        } else {
                            console.log("No audio doc found to copy.");
                        }
                    } catch (error) {
                        console.error("Error fetching audio: ", error);
                    }
                    if (mediaOption === "All Media") {
                        const seenTakeIds = new Set();
                        for (let take of takes) {
                            if (!take || take.deleted_at) {
                                continue;
                            }
                            const takeId = take.id || take.ref?.id;
                            if (!takeId || typeof takeId !== 'string') {
                                console.warn("Skipping take without valid ID during fork:", take);
                                continue;
                            }
                            if (seenTakeIds.has(takeId)) {
                                continue;
                            }
                            seenTakeIds.add(takeId);

                            const newTake = {
                                video_path: take.video_path || "",
                                thumbnail: take.thumbnail || "",
                                id: takeId,
                                shot_id: take.shot_id || "",
                                uploaded_at: take.uploaded_at || "",
                                uploader_id: take.uploader_id || "",
                                proxyLUT: state.app.settings?.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                                is_vertical: state.stories.activeStory?.is_vertical || false,
                                deleted_at: null,
                            };
                            const newRefTake = doc(newStoryRef, 'takes', takeId);
                            forkBatch.set(newRefTake, newTake, { merge: true });
                            // Copy veneers
                            const veneer = state.stories.activeVeneerLUT ? state.stories.activeVeneerLUT[takeId] : null;
                            if (veneer && veneer.ref) {
                                const newVeneerRef = doc(newStoryRef, 'veneer', takeId);
                                const oldVeneerPath = veneer.ref.path ? veneer.ref.path.toString() : "";
                                const newVeneer = {
                                    ...veneer,
                                    deleted_at: null,
                                    forked_source: oldVeneerPath,
                                    abort_reason: "forked"
                                };
                                forkBatch.set(newVeneerRef, newVeneer);
                            }
                        }
                    } else if (mediaOption === "Selected Media") {
                        const seenTakeIds = new Set();
                        for (let shot of shots) {
                            const selectedTakes = takes.filter(take => {
                                const tid = take?.id || take?.ref?.id;
                                return tid && (tid === (shot.selected_take_id || shot.last_take_id));
                            });
                            for (let take of selectedTakes) {
                                const takeId = take.id || take.ref?.id;
                                if (!takeId || typeof takeId !== 'string' || seenTakeIds.has(takeId)) continue;
                                seenTakeIds.add(takeId);

                                const newTake = {
                                    video_path: take.video_path || "",
                                    thumbnail: take.thumbnail || "",
                                    id: takeId,
                                    shot_id: take.shot_id || "",
                                    uploaded_at: take.uploaded_at || "",
                                    uploader_id: take.uploader_id || "",
                                    proxyLUT: state.app.settings?.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                                    is_vertical: state.stories.activeStory?.is_vertical || false,
                                    deleted_at: null
                                };
                                const newRefTake = doc(newStoryRef, 'takes', takeId);
                                forkBatch.set(newRefTake, newTake, { merge: true });
                                const veneer = state.stories.activeVeneerLUT ? state.stories.activeVeneerLUT[takeId] : null;
                                if (veneer && veneer.ref) {
                                    const newVeneerRef = doc(newStoryRef, 'veneer', takeId);
                                    const oldVeneerPath = veneer.ref.path ? veneer.ref.path.toString() : "";
                                    const newVeneer = {
                                        ...veneer,
                                        deleted_at: null,
                                        forked_source: oldVeneerPath,
                                        abort_reason: "forked"
                                    };
                                    forkBatch.set(newVeneerRef, newVeneer);
                                }
                            }
                        }
                    }
                    await forkBatch.commit();

                    const payload = {
                        activeStoryRef: newStoryRef,
                        setLastActive: true
                    };
                    dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: payload });
                    dispatch({ type: "CLEAR_FORK" });
                } catch (err) {
                    console.error("Fork operation failed: ", err);
                    toast.error(Messages.error.projectForkFailed);
                    dispatch({ type: "CLEAR_FORK" });
                }
            };
            forkStory();
        }
        if (state.app.forkingStory) forkIt();
    }, [state.app.forkingStory])

    // Init and Fork
    useEffect(() => {
        if (!state.app.initAndFork) return;
        if (state.app.initAndFork.fork) initializeFromPortal(state.app.initAndFork.portalId, state.app.initAndFork.fork, true);
        dispatch({ type: "CLEAR_INIT_AND_FORK", payload: {} });
    }, [state.app.initAndFork]);

    // Save Cut
    useEffect(() => {
        if (state.app.savingCut) {
            let in_offset_ms = [];
            let out_offset_ms = [];
            const storyRef = state.stories.activeStory.ref;
            const { do_tgt_cut, sequence, tgt_audio, mute_clip_audio } = state.app.savingCut;
            // settings for render
            const do_low_res = true;
            const do_high_res = true;
            if (sequence["trims"].length) {
                sequence["trims"].map((trim) => {
                    if (trim) {
                        in_offset_ms.push(trim[0]);
                        out_offset_ms.push(trim[1]);
                    } else {
                        in_offset_ms.push(0);
                        out_offset_ms.push(0);
                    }
                })
            }
            // makes a zip archive at every save  ... its a bit slow, but we can render an export button when its ready   ... or we could do a popup alert message, only after a desired export action?
            const do_export = true;
            if (do_tgt_cut) {
                if (do_low_res) {
                    console.log("LOW RES tgt RENDER video_filenames:", sequence["proxy"]);
                    const cut_request_tgt = {
                        save_type: "tgt",
                        high_quality: "False",
                        video_filenames: sequence["proxy"],
                        audio: tgt_audio, // todo: get from import_media if it has audio? place here if there is only one chapter with clips in order?  story.last_audio
                        version: null,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                        tgt: { media: sequence["tgt"] },
                    };
                    const cut_id_tgt = uuidv4();
                    const cutRefTgt = doc(storyRef, "cuts", cut_id_tgt)
                    setDoc(cutRefTgt, cut_request_tgt, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                        })
                        .catch((error) => {
                            console.log("Error creating HQ cut request:", cut_request_tgt);
                        });
                }

                if (do_high_res) {
                    const cut_request_tgt = {
                        save_type: "tgt",
                        high_quality: "True",
                        video_filenames: sequence["files"],
                        audio: tgt_audio,
                        version: null,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                        tgt: { media: sequence["tgt"] },
                    };
                    const cut_id_tgt = uuidv4();
                    const cutRefTgt = doc(storyRef, "cuts", cut_id_tgt)
                    setDoc(cutRefTgt, cut_request_tgt, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                            logEvent(analytics, 'SAVE_CUT')
                        })
                        .catch((error) => {
                            console.log("Error creating HQ cut request:", cut_request_tgt);
                        });

                    if (do_export) {
                        const export_request_tgt = {
                            video_filenames: sequence["files"],
                            audio: tgt_audio,
                            version: null,
                            dst_zip_url: null,
                            created_at: serverTimestamp(),
                            mute_clip_audio: mute_clip_audio,
                            tgt: { media: sequence["tgt"] },
                        };
                        const export_id_tgt = cut_id_tgt; // uuidv4()
                        const exportRefTgt = doc(storyRef, "exports", export_id_tgt)
                        setDoc(exportRefTgt, export_request_tgt, { merge: true })
                            .then(() => {
                                dispatch({ type: "START_EXPORT", payload: {} });
                            })
                            .catch((error) => {
                                console.log(
                                    "Error creating export request:",
                                    export_request_tgt
                                );
                            });
                        console.log("tried export_request_tgt:", export_request_tgt);
                    }
                }
            } else {
                const normal_audio = state.stories.activeStory.audio ? state.stories.activeStory.audio : "silent";

                // non tgt cut is a render
                if (do_low_res) {
                    console.log("LOW RES RENDER video_filenames:", sequence["proxy"]);
                    const cut_id = uuidv4();
                    const cut_request = {
                        save_type: "basic",
                        high_quality: "False",
                        video_filenames: sequence["proxy"],
                        audio: normal_audio,// todo:  do we want this to be "silent"?
                        version: null,
                        in_offset_ms: in_offset_ms,
                        out_offset_ms: out_offset_ms,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                    };

                    const cutRef = doc(storyRef, "cuts", cut_id)
                    setDoc(cutRef, cut_request, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                        })
                        .catch((error) => {
                            console.log("Error creating cut request:", cut_request);
                        });
                }

                if (do_high_res) {
                    console.log("HIGH RES RENDER video_filenames:", sequence["files"]);
                    // note: handling of video_path is not addressed for tgt videos (e.g. tgt_stream_video exist in low res, not hgh)
                    //   could:  backfill low res mized with high, or propogate a link to a private high res file
                    const cut_request_hq = {
                        save_type: "basic",
                        high_quality: "True",
                        video_filenames: sequence["files"], // this is empty
                        in_offset_ms: in_offset_ms,
                        out_offset_ms: out_offset_ms,
                        audio: normal_audio,
                        version: null,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                    };

                    const cut_id_hq = uuidv4();
                    const cutRefHq = doc(storyRef, "cuts", cut_id_hq)
                    setDoc(cutRefHq, cut_request_hq, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                            logEvent(analytics, 'SAVE_CUT')
                        })
                        .catch((error) => {
                            console.log("Error creating HQ cut request:", cut_request_hq);
                        });

                    if (do_export) {
                        const export_request = {
                            video_filenames: sequence["files"],
                            audio: normal_audio, // null  ... note:  we are allowing the audio from tgt to come out in export, even if trimming media is not complete ...
                            version: null,
                            in_offset_ms: in_offset_ms,
                            out_offset_ms: out_offset_ms,
                            dst_zip_url: null,
                            created_at: serverTimestamp(),
                            tgt: null,
                            mute_clip_audio: mute_clip_audio,
                        };
                        const export_id = cut_id_hq; // uuidv4()
                        const exportRefTgt = doc(storyRef, "exports", export_id)
                        setDoc(exportRefTgt, export_request, { merge: true })
                            .then(() => {
                                dispatch({ type: "START_EXPORT", payload: {} });
                            })
                            .catch((error) => {
                                console.log("Error creating export request:", export_request);
                            });
                        console.log("tried export_request (non_tgt):", export_request);
                    }
                }
            }

            dispatch({ type: "CLEAR_LAST_CUT", payload: {} });
            dispatch({ type: "CLEAR_SAVE_CUT", payload: {} });
            dispatch({ type: "CLEAR_LAST_EXPORT", payload: {} });
        }
    }, [state.app.savingCut])


    // propate last_cut to cloud.  ONLY used for clearing after a save. 
    useEffect(() => {
        const activeStoryRef = state.stories.activeStoryRef
        if (!activeStoryRef) return;
        // patch for onSnapshot subscription management so it does not impact active story
        if (window.location.href.indexOf(state.stories.activeStory.portal_id) == -1) return;
        const unsetLastCut = async () => {
            return await setDoc(activeStoryRef, { "last_cut": null }, { merge: true })
                .catch((error) => {
                    console.log('Error updating last_cut:', error)
                    return error
                })
        }
        if (state.app.savingCut && state.stories.activeStory.last_cut === null) {
            unsetLastCut()
        }
    }, [state.stories.activeStory.last_cut])

    // update parts onchange
    /*useEffect(() => {
        const activeStoryRef = state.stories.activeStoryRef
        if(!activeStoryRef) return;
        const updateParts = async () => {
            return await setDoc(activeStoryRef, {"parts": state.stories.activeStory.parts}, {merge:true})
            .catch((error) => {
                console.log('Error updating parts:', error)
                return error
                })
        }
        if (state.stories.activeStory.parts.length !== 0) {
            updateParts()
        }
    }, [state.stories.activeStory.parts])*/


    // propate last_export to cloud.  used for clearing after a save. 
    useEffect(() => {
        if (!state.stories.activeStoryRef) return;
        // patch for onSnapshot subscription management so it does not impact active story
        if (window.location.href.indexOf(state.stories.activeStory.portal_id) == -1) return;
        const activeStoryRef = state.stories.activeStoryRef
        const setStorysLastExport = async (last_export) => {
            return await setDoc(state.stories.activeStoryRef, { "last_export": last_export }, { merge: true })
                .catch((error) => {
                    console.log('Error updating last_export:', last_export)
                    return error
                })
        }
        if (state.stories.activeStory.last_export === null) {
            setStorysLastExport(state.stories.activeStory.last_export)
        }
    }, [state.stories.activeStory.last_export])


    // STORY LIST
    // Listen for updates to story list (based on currentUser)
    useEffect(() => {
        if (!currentUser) return
        let unSubscribe = () => { };
        const getStories = () => {
            try {
                const storiesRef = collection(doc(db, 'users', currentUser?.uid), 'stories')

                unSubscribe = onSnapshot(storiesRef, async querySnapshot => {
                    const storyList = [];
                    querySnapshot.forEach(async (doc) => {
                        if (doc.exists() && (!doc.get("deleted_at") || doc.get("deleted_at") == null)) {
                            const data = doc.data();
                            const story = { ref: doc.ref, ...data }
                            storyList.push(story)
                        }
                    })
                    dispatch({ type: "UPDATE_STORY_LIST", payload: { storyList } });
                });
            } catch (error) {
                console.log('Error subscribing to updates for story list: ', error);
            }
        }
        getStories();
        return () => unSubscribe();
    }, [dispatch, currentUser])

    // // SHARED STORY LIST
    // // Listen for updates to shared story list (based on currentUser)
    // useEffect(() => {
    //     if (!currentUser) return
    //     let unSubscribe = () => { };
    //     try {
    //         const userRef = await setDoc(doc(db, 'users').doc(currentUser.uid);
    //         const storiesRef = db.collectionGroup('stories')
    //             .where("sharedWith", "array-contains", userRef)
    //             .where("deleted_at", "==", null);
    //         unSubscribe = storiesRef.onSnapshot(querySnapshot => {
    //             const storyList = [];
    //             querySnapshot.forEach((doc) => {
    //                 if (doc.exists) {
    //                     const data = doc.data();
    //                     const story = { ref: doc.ref, ...data }
    //                     storyList.push(story)
    //                 }
    //             })
    //             dispatch({ type: "UPDATE_SHARED_STORY_LIST", payload: { sharedStoryList: storyList } });
    //         })
    //     } catch (error) {
    //         console.log('Error subscribing to updates for shared story list: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }


    // }, [dispatch, currentUser])

    // // ALBUM LIST
    // // Listen for updates to album list (based on currentUser)
    // useEffect(() => {
    //     if (currentUser) {
    //         let unSubscribe = () => { };
    //         try {
    //             const albumsRef = await setDoc(doc(db, 'albums')
    //                 .where("status", "==", "display")
    //                 .where("deleted_at", "==", null);
    //             unSubscribe = albumsRef.onSnapshot(querySnapshot => {
    //                 const albumList = [];
    //                 querySnapshot.forEach((doc) => {
    //                     if (doc.exists) {
    //                         const data = doc.data();
    //                         const album = { ref: doc.ref, ...data }
    //                         albumList.push(album)
    //                     }
    //                 })
    //                 dispatch({ type: "UPDATE_ALBUM_LIST", payload: { albumList } });
    //             })
    //         } catch (error) {
    //             console.log('Error subscribing to updates for album list: ', error)
    //         } finally {
    //             return () => unSubscribe();
    //         }


    //     }
    // }, [dispatch, currentUser])

    // ACTIVE STORY
    // Listen for updates to activeStory (based on state.activeStoryRef)
    // Password gate: checkPortalPass runs before attaching the snapshot —
    // password-protected stories are never exposed until the password is verified.
    useEffect(() => {
        if (!state.stories.activeStoryRef || !Object.keys(state.stories.activeStoryRef).length) return;
        const activeStoryRef = state.stories.activeStoryRef;
        let unSubscribe = () => { };

        const getActiveStory = async () => {
            if (activeStoryRef) {
                // Resolve portalId: prefer cache (already fetched), then state, then URL
                let portalId = portalCacheRef.current?.id || state.app.portal?.id;
                if (!portalId) {
                    const urlMatch = matchPath({ path: '/p/:id', end: false }, window.location.pathname);
                    portalId = urlMatch?.params?.id || null;
                }
                if (!portalId) return;

                // Password guard: do not attach snapshot until portal password is verified
                const pass = await checkPortalPass(portalId);
                if (!pass) return;

                try {
                    unSubscribe = onSnapshot(activeStoryRef, async querySnapshot => {
                        if (!querySnapshot) return;
                        let activeStory = querySnapshot.data();
                        if (!activeStory) return;
                        // Only apply when viewing this story's portal URL or transitioning
                        // from /storyboards (ref set before navigate completes)
                        const onThisPortal = activeStory.portal_id ? window.location.href.indexOf(activeStory.portal_id) !== -1 : true;
                        const onStoryboards = window.location.pathname.indexOf('/storyboards') !== -1;
                        if (!onThisPortal && !onStoryboards) return;
                        activeStory.autostory = importsCountRef.current > 0;
                        activeStory.ref = activeStoryRef;
                        // Back-compat < 0.3.21: append an ID to each part if missing
                        if (activeStory?.parts.length && !activeStory.parts[0].id) {
                            for (var i = 0; i < activeStory.parts.length; i++) {
                                if (!('id' in activeStory.parts[i])) {
                                    activeStory.parts[i] = { ...activeStory.parts[i], ...{ id: nanoid() } };
                                }
                            }
                        }
                        if (activeStory.deleted_at) activeStory.isDeleted = true;
                        dispatch({ type: "UPDATE_ACTIVE_STORY", payload: { activeStory: activeStory } });
                    });
                } catch (error) {
                    console.log('Error subscribing to updates for story list: ', error);
                }
            } else {
                dispatch({ type: "CLEAR_ACTIVE_STORY_REF" });
            }
        };
        getActiveStory();
        return () => unSubscribe();
    }, [state.stories.activeStoryRef, currentUser, state.app.portal?.id]);

    // Real-time listener for project credit balance on /stories/{storyId}/credits/0
    useEffect(() => {
        if (!currentUser || !state.stories.activeStoryRef) return;
        const creditsRef = doc(state.stories.activeStoryRef, 'credits', '0');
        const unsubscribe = onSnapshot(creditsRef, (snap) => {
            if (snap.exists()) {
                dispatch({ type: "UPDATE_ACTIVE_STORY_CREDITS", payload: { credits: snap.data().credits || 0 } });
            } else {
                dispatch({ type: "UPDATE_ACTIVE_STORY_CREDITS", payload: { credits: null } });
            }
        }, (error) => {
            console.log('Error listening to project credit data:', error);
        });
        return () => unsubscribe();
    }, [state.stories.activeStoryRef, currentUser]);

    // update story style
    useEffect(() => {
        const run = async () => {
            if (!currentUser || !state.stories.activeStoryRef) return;
            if (state.stories.activeStoryRef && state.stories.activeStory.storyStyle && state.stories.activeStory.storyStyle !== "") {
                await setDoc(state.stories.activeStoryRef, { "storyStyle": state.stories.activeStory.storyStyle }, { merge: true })
                    .catch((error) => {
                        console.error("Error updating story style:", error);
                    });
            }
        };
        run();
    }, [state.stories.activeStory.storyStyle])

    // update credits — writes to /credits/0 subcollection
    useEffect(() => {
        if (!currentUser || !state.app.updateCredits || state.app.updateCredits == 0) return;
        const balanceRef = doc(db, 'users', currentUser.uid, 'credits', '0');

        //[test]
        // draft.userData.credits = draft.userData.credits + action.payload.credits
        setDoc(balanceRef, { "credits": increment(state.app.updateCredits), "updated_at": serverTimestamp() }, { merge: true })
        dispatch({ type: "UPDATE_USER_CREDITS", payload: { credits: 0 } });
    }, [state.app.updateCredits, currentUser])

    // Project credit transfer — atomic batch write between user and project credit balances
    useEffect(() => {
        if (!currentUser || !state.app.projectCreditTransfer) return;
        const { direction, amount, storyRef } = state.app.projectCreditTransfer;
        if (!storyRef || !amount || amount <= 0) return;

        const handleTransfer = async () => {
            const userBalanceRef = doc(db, 'users', currentUser.uid, 'credits', '0');
            const projectBalanceRef = doc(storyRef, 'credits', '0');
            const fundedCollRef = collection(projectBalanceRef, 'funded');

            const transferBatch = writeBatch(db);

            if (direction === 'toProject') {
                // User → Project
                transferBatch.set(userBalanceRef, { credits: increment(-amount), updated_at: serverTimestamp() }, { merge: true });
                transferBatch.set(projectBalanceRef, { credits: increment(amount), updated_at: serverTimestamp() }, { merge: true });
                const fundedDocRef = doc(fundedCollRef);
                transferBatch.set(fundedDocRef, {
                    userId: currentUser.uid,
                    amount: amount,
                    source: 'transfer',
                    date: serverTimestamp(),
                });
            } else if (direction === 'fromProject') {
                // Project → User
                transferBatch.set(projectBalanceRef, { credits: increment(-amount), updated_at: serverTimestamp() }, { merge: true });
                transferBatch.set(userBalanceRef, { credits: increment(amount), updated_at: serverTimestamp() }, { merge: true });
                const fundedDocRef = doc(fundedCollRef);
                transferBatch.set(fundedDocRef, {
                    userId: currentUser.uid,
                    amount: -amount,
                    source: 'transfer',
                    date: serverTimestamp(),
                });
            }

            try {
                await transferBatch.commit();
                // Refresh the user's local credit balance from Firestore.
                // We use UPDATE_USER_CREDIT_BALANCE (local-only, does NOT trigger the
                // update-credits useEffect Firestore write) to avoid double-deducting —
                // the batch already wrote the correct increment to the user balance doc.
                const updatedUserSnap = await getDoc(userBalanceRef);
                if (updatedUserSnap.exists()) {
                    const d = updatedUserSnap.data();
                    dispatch({
                        type: "UPDATE_USER_CREDIT_BALANCE",
                        payload: {
                            credits: d.credits ?? 0,
                            processingCredits: d.processingCredits ?? false,
                            lastCredits: d.lastCredits ?? null,
                        },
                    });
                }
                // Read back the updated project balance and update state directly
                const updatedSnap = await getDoc(projectBalanceRef);
                if (updatedSnap.exists()) {
                    dispatch({ type: "UPDATE_ACTIVE_STORY_CREDITS", payload: { credits: updatedSnap.data().credits || 0 } });
                }
            } catch (error) {
                console.error('Error transferring project credits:', error);
            }
            dispatch({ type: "CLEAR_PROJECT_CREDIT_TRANSFER", payload: {} });
        };
        handleTransfer();
    }, [state.app.projectCreditTransfer, currentUser]);

    useEffect(() => {
        if (!currentUser || !state.app.createVeneer) return;
        const { veneer_id, veneer, cost, userData, story } = state.app.createVeneer;
        if (userData.credits && userData.credits < cost) {
            dispatch("UPDATE_USER_CREDITS", { credits: 0 });
            return;
        }
        const handleVeneer = async () => {
            // Compute credit split between project and user
            const storyRef = state.stories.activeStoryRef;
            const { projectAmount, userAmount, payer } = await computeCreditSplit(
                storyRef, cost, currentUser.uid
            );

            // Write payer info onto the veneer doc
            const veneerWithPayer = { ...veneer, payer: payer };
            const veneerRef = doc(story.ref, 'veneer', veneer_id)
            await setDoc(veneerRef, veneerWithPayer, { merge: true });

            dispatch({ type: "START_VENEER", payload: {} });

            // Deduct from project credits if applicable
            if (projectAmount > 0) {
                const projectBalanceRef = doc(storyRef, 'credits', '0');
                await setDoc(projectBalanceRef, { credits: increment(-projectAmount), updated_at: serverTimestamp() }, { merge: true });
            }
            // Deduct from user credits
            if (userAmount > 0) {
                dispatch({ type: "UPDATE_USER_CREDITS", payload: { credits: -userAmount } });
            }

            if (story.isCompetition && (story.timerStart == undefined || story.timerStart == null)) {
                const timerStart = Date.now();
                const payload = { timerStart: timerStart };
                dispatch({ type: "START_STORY_TIMER", payload: payload });
            }
            const takePayload = {
                video_path: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_path'] : assets['processing_veneer_thumb_path'],
                thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_gif'] : assets['processing_veneer_thumb_gif'],
                shot_id: veneer.shot_id,
                take_id: veneer_id,
                source: "generation",
                is_vertical: state.stories.activeStory?.is_vertical || false
            }
            dispatch({ type: "ADD_TAKE_TO_SHOT", payload: takePayload })
            const shotPayload = {
                take_id: veneer_id,
                image: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_jpg'] : assets['processing_veneer_thumb_jpg'],
                stream_path: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_streampath'] : assets['processing_veneer_streampath'],
                shot_id: veneer.shot_id
            };
            dispatch({ type: "UPDATE_SHOT_LAST_TAKE", payload: shotPayload })
            state.app.userIsStoryOwner ? logEvent(analytics, 'OWNER_VENEER') : logEvent(analytics, 'COMMUNITY_VENEER')
            dispatch({ type: "CLEAR_CREATE_VENEER", payload: {} });
        }
        /*.catch((error) => {
            console.log("Error creating veneer request:", veneer);
        });*/
        handleVeneer();
    }, [state.app.createVeneer, currentUser]);


    // ACTIVE STORY SETTINGS
    // Listen for updates to activeStory (based on state.activeStoryRef)
    useEffect(() => {
        if (!state.stories.activeStory.ref) return
        let unSubscribeSettings = () => { };
        try {
            const settingsRef = doc(state.stories.activeStory.ref, 'settings', '0')
            let did_dispatch = false;
            unSubscribeSettings =
                onSnapshot(settingsRef, (snapDoc => {
                    if (snapDoc.exists()) {
                        const settings = snapDoc.data();
                        dispatch({ type: "UPDATE_STORY_SETTINGS", payload: { settings: settings } });
                        did_dispatch = true
                    } else {
                        if (currentUser) {
                            const initSettings = initialState.app.settings;
                            dispatch({ type: "UPDATE_STORY_SETTINGS", payload: { settings: initSettings } });
                            setDoc(settingsRef, initSettings, { merge: true })
                        }
                    }
                }));

        } catch (error) {
            const msg = error.message
            console.log('Error subscribing to updates for story settings: ', msg)
            if (msg.search("empty") > 1) {
                console.log('EMPTY DATA for story settings: ', msg)
            }
            else {
                console.log('ANOTHER ERROR for story settings: ', error)
            }
        } finally {
            return () => unSubscribeSettings();
        }
    }, [state.stories.activeStory.ref]);

    // SETTINGS
    // Update story settings
    useEffect(() => {
        if (!state.stories.activeStoryRef) return
        const updateSettings = async () => {
            try {
                const settingsRef = doc(state.stories.activeStoryRef, 'settings', '0')
                await setDoc(settingsRef, state.app.settings, { merge: true })
            } catch (error) {
                const msg = error.message
                console.log('Error updating story settings: ', msg)
            }
            dispatch({ type: "CLEAR_NEW_SETTINGS" });
        }
        updateSettings();

    }, [state.app.newSettings]);

    // PORTAL
    // Listen for requests to make a new portal
    useEffect(() => {

        if (!currentUser || !state.stories.activeStoryRef) {
            return
        }
        if (!state.app.portal.requested_id) {
            console.log("no portal requested ID")
            return
        }

        if (state.stories.activeStory.portal_id) {
            console.log("WARNING:   Portal already exists, don't make another", state.stories.activeStory.portal_id)
            return

            //ToDO later: enable user request for portal URL
            // Q: If there is an existing portal for this story, should we delete it?
            //
        }

        const portal_id = state.app.portal.requested_id
        const createPortal = async (portal_id) => {
            const portalRef = doc(db, 'portals', portal_id);
            const newPortal = {
                creator_id: currentUser.uid,
                story_id: state.stories.activeStoryRef.id,
                created_at: serverTimestamp(),
                deleted_at: null,
                requirePass: null
            }

            return await function () {
                const ref = setDoc(portalRef, newPortal, { merge: true });
                return ref;
            }.catch((error) => {
                console.log('Error creating new portal:', portal_id)
                return error
            })
        }

        const setStorysPortal = async (portal_id) => {
            return await function () {
                const story = setDoc(state.stories.activeStoryRef, { "portal_id": portal_id }, { merge: true });
                console.log("Story updated successfully.");
                return story;
            }
                .catch((error) => {
                    console.log('Error updating portal id:', portal_id)
                    return error
                })
        }

        // ASIDE:  it would be nice if these two actions were atomic
        // maybe serverside logic would help
        createPortal(portal_id)
        setStorysPortal(portal_id)
        dispatch({ type: "CLEAR_REQUESTED_PORTAL" });


        //await createPortal(portal_id)

    }, [state.app.portal.requested_id]);

    // Listen for updates to STORY (based on change in PORTAL ID)
    useEffect(() => {
        // const portal = state.app.portal
        // if (!portal.id) {
        //     // A null id will get initialized to the portal id in the URL
        //     let portal_id = "non-portal"
        //     if (loc.pathname.match('/p/*')){
        //         portal_id = (loc.pathname.match('/p/*')[0] === '/p/') ? loc.pathname.substring(3) : "non-portal"        
        //         dispatch({ type: "UPDATE_PORTAL", payload: { portal_id } });
        //     } else {
        //         console.log("NONMATCH PORTAL")
        //     }
        // }
        // if (portal.id) {
        //     console.log("EFFECT PORTAL_ID", portal.id)
        //     // this logic could move to initializeActiveStoryRef

        //     const portalRef =  await setDoc(doc(db, 'portals').doc(portal.id) 
        //     const _portal = portalRef.get().then((doc) => {
        //         if (doc.exists) {
        //             console.log("PORTAL data:", doc.data());
        //             const user_id = doc.data().creator_id
        //             const story_id = doc.data().story_id

        //             const activeStoryRefX =  await setDoc(doc(db, 'users').doc(user_id).collection('stories').doc(story_id) // db.database().ref('portal/' + portal_id + '/story_path');
        //             dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { activeStoryRefX } });

        //             let unSubscribe = () => { };
        //             //try {     
        //                     unSubscribe =
        //                     activeStoryRefX.onSnapshot(doc => {
        //                         if (doc.exists && !doc.get("deleted_at")) {
        //                             const activeStory = doc.data();
        //                             activeStory.ref = doc.ref;
        //                             dispatch({ type: "UPDATE_ACTIVE_STORY", payload: { activeStory } });
        //                         } else {
        //                             console.log("Story? No such document!");
        //                             //dispatch({ type: "CLEAR_ACTIVE_STORY_REF" });
        //                         }
        //                     });
        //             // catch (error) {
        //             //         console.log('Error querying data for portal defined Story: ', error)
        //             //     } finally {
        //             //         return () => unSubscribe();
        //             //     }
        //             // }

        //         } else {
        //             // doc.data() will be undefined in this case
        //             console.log("No such document!");
        //         }
        //     }).catch((error) => {
        //         console.log("Error getting document:", error);
        //     });
        // }
    }, [dispatch, state.app.portal])


    // LAST ACTIVE STORY
    // Set lastActiveStory in /users/{uid}/state/0 subcollection (isolated from credit listener)
    useEffect(() => {
        if (!currentUser || !state.stories.activeStory || !state.stories.activeStory.ref) return

        if (state.stories.setLastActive && state.stories.lastActiveStory?.portalId != state.stories.activeStory.portal_id) {
            const stateRef = doc(db, 'users', currentUser.uid, 'state', '0');
            const lastActiveObj = {
                storyId: state.stories.activeStory.ref.id,
                portalId: state.stories.activeStory.portal_id
            };
            const setLast = () => {
                setDoc(stateRef, { "lastActiveStory": lastActiveObj }, { merge: true })
                    .then(() => {
                        dispatch({ type: "SET_LAST_ACTIVE", payload: { lastActiveStory: lastActiveObj } })
                    })
                    .catch((error) => {
                        console.log('Error setting lastActiveStory: ', error)
                    })
            }
            setLast();
            dispatch({ type: "CLEAR_LAST_ACTIVE", payload: {} })
        }
    }, [dispatch, state.stories.activeStory, currentUser]);


    // useEffect(() => {
    //     if (!currentUser || !state.stories.activeStory || !state.stories.activeStory.ref) {
    //         dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: false } });
    //         return;
    //     }

    //     let userIsStoryOwner = false;
    //     const storyPathArray = state.stories.activeStory.ref.path.split("/");
    //     const userIndex = storyPathArray.findIndex(item => item === "users") + 1;
    //     const ownerIdInPath = storyPathArray[userIndex];

    //     console.log("Current User ID:", currentUser.uid);
    //     console.log("Owner ID in Path:", ownerIdInPath);

    //     if (currentUser.uid === ownerIdInPath) {
    //         userIsStoryOwner = true;
    //     }

    //     console.log("Final Calculated userIsStoryOwner:", userIsStoryOwner);
    //     dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner } });
    // }, [currentUser, state.stories.activeStoryRef, state.stories.activeStory]);


    useEffect(() => {
        if (currentUser && currentUser.uid == state.app.portal?.creator_id) {
            //setStoryOwner();
            dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: true } });
        } else if (state.app.portal?.creator_id) {
            dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: false } });
        } else if (currentUser && state.stories.activeStoryRef?.path) {
            // Fallback: derive ownership from the activeStoryRef path (e.g. users/{uid}/stories/{id})
            // This covers the case where a new story is created from /new and the portal
            // hasn't been fetched yet (portal.creator_id is undefined).
            const pathParts = state.stories.activeStoryRef.path.split("/");
            const userIndex = pathParts.findIndex((item) => item === "users") + 1;
            if (pathParts[userIndex] && currentUser.uid === pathParts[userIndex]) {
                if (!state.app.userIsStoryOwner) dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: true } });
            }
        }
        // Compute granular project role from portal data
        const role = computeUserRole(currentUser, state.app.portal);
        if (role !== state.app.userProjectRole) {
            dispatch({ type: "SET_USER_PROJECT_ROLE", payload: { userProjectRole: role } });
        }
    }, [currentUser, state.app.portal, state.stories.activeStoryRef]);

    // INVITE ACCEPTANCE
    // When a user lands on /p/{portal_id}?invite={code}, accept the invite
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const inviteCode = params.get('invite');
        if (!inviteCode || !state.app.portal?.id) return;

        if (!currentUser) {
            // Store the full URL so the user is redirected back after login
            sessionStorage.setItem('afterLoginRedirect', window.location.pathname + window.location.search);
            return;
        }

        const members = state.app.portal.members || [];
        const match = members.find(m => m.invite_code === inviteCode);

        if (match && match.status === "pending") {
            dispatch({
                type: "ACCEPT_PROJECT_INVITE",
                payload: {
                    invite_code: inviteCode,
                    uid: currentUser.uid,
                    portal_id: state.app.portal.id,
                }
            });
            // Clean the invite param from the URL
            window.history.replaceState({}, '', `/p/${state.app.portal.id}`);
            toast.success("You've been added as a team member!");
        } else if (match && match.status === "accepted") {
            // Already accepted — just clean the URL
            window.history.replaceState({}, '', `/p/${state.app.portal.id}`);
        }
    }, [currentUser, state.app.portal]);

    // TEAM STORYBOARDS
    // Query portals where the current user is a member, for the Team Storyboards tab
    useEffect(() => {
        if (!currentUser) return;
        let unsubscribe = () => { };
        try {
            const q = query(
                collection(db, 'portals'),
                where('member_uids', 'array-contains', currentUser.uid)
            );
            unsubscribe = onSnapshot(q, async (snapshot) => {
                const teamStories = [];
                for (const portalDoc of snapshot.docs) {
                    const portalData = portalDoc.data();
                    try {
                        const storyRef = doc(db, 'users', portalData.creator_id, 'stories', portalData.story_id);
                        const storySnap = await getDoc(storyRef);
                        if (storySnap.exists() && !storySnap.data().deleted_at) {
                            teamStories.push({
                                ...storySnap.data(),
                                ref: storySnap.ref,
                                portal_id: portalDoc.id
                            });
                        }
                    } catch (err) {
                        console.error('Error fetching team story:', err);
                    }
                }
                dispatch({ type: "UPDATE_SHARED_STORY_LIST", payload: { sharedStoryList: teamStories } });
            });
        } catch (error) {
            console.error('Error querying team storyboards:', error);
        }
        return () => unsubscribe();
    }, [currentUser]);

    // UPDATE MEMBERS — persist member changes via Cloud Function
    // Follows the set-then-clear pattern (like updateUserState)
    useEffect(() => {
        if (!state.app.updateMembers) return;
        const payload = state.app.updateMembers;
        const updatePortalMembers = httpsCallable(functionsInstance, 'updatePortalMembers');
        updatePortalMembers(payload)
            .then((result) => {
                console.log('Members persisted via Cloud Function');
            })
            .catch((error) => {
                console.error('Error persisting members:', error);
            });
        dispatch({ type: 'CLEAR_UPDATE_MEMBERS' });
    }, [state.app.updateMembers]);


    // // ACTIVE CARD LIST is similar to ACTIVE SHOT LIST ... see below
    // // Listen for changes to activeShotList
    // useEffect(() => {
    //     if (!currentUser || !state.stories.activeStoryRef) return
    //     let unSubscribe = () => { };
    //     try {
    //         const activeStoryRef = state.stories.activeStoryRef
    //         const shotsRef = activeStoryRef
    //             .collection('cards')
    //             .where('deleted_at', '==', null)
    //             .where('display', '==', true)
    //             .orderBy('orientation', 'desc')
    //             .orderBy('created_at', 'asc')
    //         unSubscribe = shotsRef.onSnapshot(querySnapshot => {
    //             const activeShotList = [];
    //             const sourceSet = [];
    //             querySnapshot.forEach((doc) => {
    //                 if (doc.exists) {
    //                     const card = doc.data();
    //                     card.ref = doc.ref;
    //                     activeShotList.push(card)
    //                     if (card.image_source_id) {
    //                         sourceSet.push(card.image_source_id)
    //                     }
    //                 }
    //             })
    //             dispatch({ type: "UPDATE_ACTIVE_SHOT_LIST", payload: { activeShotList } });
    //             dispatch({ type: "UPDATE_ALBUM_SOURCE_SET", payload: { sourceSet } });
    //             dispatch({ type: "APP_LOADED", payload: {} });
    //         })
    //     }
    //     catch (error) {
    //         console.log('Error querying data for activeShotList: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }
    // }, [dispatch, state.stories.activeStoryRef, currentUser]);

    // ACTIVE SHOT LIST
    useEffect(() => {
        if (!state.stories.activeStoryRef) return
        const activeStoryRef = state.stories.activeStoryRef
        if (!activeStoryRef) return

        // Fix 5: Collect all unsubscribe functions for proper cleanup
        const unsubscribes = [];

        // Shots listener
        try {
            const shotRef = query(collection(activeStoryRef, 'shots'), where('deleted_at', '==', null))
            const unsubShots = onSnapshot(shotRef, querySnapshot => {
                const activeShotList = [];
                querySnapshot.forEach((doc) => {
                    if (doc.exists()) {
                        var shot = doc.data();
                        shot.ref = doc.ref;
                        shot.id = shot.id || doc.id;
                        activeShotList.push(shot)
                    }
                })
                dispatch({ type: "UPDATE_ACTIVE_SHOT_LIST", payload: { activeShotList } });
                dispatch({ type: "APP_LOADED", payload: {} });
            });
            unsubscribes.push(unsubShots);
        }
        catch (error) {
            console.log('Error querying data for activeShotList: ', error)
        }

        // Takes listener
        try {
            const takesRef = query(collection(activeStoryRef, 'takes'), where('deleted_at', '==', null))
            const unsubTakes = onSnapshot(takesRef, querySnapshot => {
                const activeTakeList = [];
                const activeRefList = [];
                querySnapshot.forEach(snapDoc => {
                    if (snapDoc.exists()) {
                        var take = snapDoc.data();
                        take.ref = snapDoc.ref;
                        take.id = take.id || snapDoc.id;
                        if (take.is_ref) activeRefList.push(take);
                        if (take.shot_id && take.shot_id !== "reference") activeTakeList.push(take)
                    }
                })
                dispatch({ type: "UPDATE_ACTIVE_TAKE_LIST", payload: { activeTakeList } });
                dispatch({ type: "UPDATE_ACTIVE_REF_LIST", payload: { activeRefList } });
            });
            unsubscribes.push(unsubTakes);
        }
        catch (error) {
            console.log('Error querying data for activeTakeList: ', error)
        }

        const getImportsCount = async () => {
            try {
                const snap = await getDocs(collection(activeStoryRef, 'imports'));
                importsCountRef.current = snap.docs.length;
            } catch (e) {
                importsCountRef.current = 0;
            }
        };
        getImportsCount();

        // User state (one-time fetch, not a listener)
        const getUserState = async () => {
            try {
                if (!activeStoryRef || !currentUser) return

                dispatch({ type: "UPDATE_GENERATE_FETCHED", payload: false });
                console.log("getUserState", state.generate, currentUser.uid)
                await getDoc(doc(activeStoryRef, "user_state", currentUser.uid)).then((doc) => {
                    if (doc.exists()) {
                        const data = doc.data();
                        if (data.hasOwnProperty("userRefs")) {
                            dispatch({ type: "UPDATE_USER_REFS", payload: data.userRefs });
                        }
                        if (data.hasOwnProperty("prompt")) {
                            dispatch({ type: "UPDATE_PROMPT", payload: data.prompt });
                        }
                        if (data.hasOwnProperty("model")) {
                            dispatch({ type: "UPDATE_SELECTED_MODEL", payload: data.model });
                            // systemModel starts equal to the user's saved preference;
                            // may diverge within a session but is never written back to Firebase.
                            dispatch({ type: "UPDATE_SYSTEM_MODEL", payload: data.model });
                        }
                        if (data.hasOwnProperty("method") && data.method) {
                            dispatch({ type: "UPDATE_SELECTED_METHOD", payload: data.method });
                        }
                        /*if (data.hasOwnProperty("output")) {
                            dispatch({ type: "UPDATE_SELECTED_OUTPUT", payload: data.output });
                        }*/
                    }
                    dispatch({ type: "UPDATE_GENERATE_FETCHED", payload: true });
                });
            } catch (error) {
                console.log('Error querying data for userRefs: ', error)
            }
        };
        getUserState();

        // Audio (one-time fetch)
        const getAudio = async () => {
            try {
                await getDoc(doc(activeStoryRef, "audio", "0")).then((doc) => {
                    if (doc.exists()) dispatch({ type: "UPDATE_AUDIO", payload: doc.data() });
                    else dispatch({ type: "UPDATE_AUDIO", payload: {} });
                })
            } catch (error) {
                console.log('Error querying data for audio: ', error)
            }
        };
        getAudio();

        // Fix 5: Return composed cleanup that unsubscribes all listeners
        return () => {
            unsubscribes.forEach(unsub => unsub());
        };

    }, [dispatch, state.stories.activeStoryRef]);

    useEffect(() => {

    }, [state.stories.activeStory.audio]);

    useEffect(() => {
        // migrate audio from last_audio to audio[0]
        if (state.stories.activeStory.last_audio && !state.app.migratingAudio) dispatch({ type: "MIGRATE_AUDIO", payload: {} });
    }, [state.stories.activeStory.last_audio])

    useEffect(() => {
        const run = async () => {
            if (!state.app.migratingAudio || !state.stories.activeStory) return;
            const storyRef = state.stories.activeStoryRef;
            const audioDocRef = doc(storyRef, "audio", "0");
            const activeStory = state.stories.activeStory;
            const index = activeStory.last_audio.lastIndexOf("/");
            const audioDoc = {
                audio_path: activeStory.last_audio,
                file_name: activeStory.audio_file_name ? activeStory.audio_file_name : activeStory.last_audio.substring(index + 1),
                uploaded_at: serverTimestamp(),
                uploader_id: currentUser.uid,
            };
            setDoc(audioDocRef, audioDoc).then(() => {
                if (activeStory.audio_file_name) setDoc(storyRef, { last_audio: null, audio_file_name: null }, { merge: true })
                setDoc(storyRef, { last_audio: null }, { merge: true }).then(() => {
                    dispatch({ type: "END_MIGRATING_AUDIO", payload: {} })
                })
            })
        };
        run();
    }, [state.app.migratingAudio])




    // // NUM SHOTS  -  just use shots.length 
    // // Listen for changes to activeStory
    // useEffect(() => {
    //     if (!state.stories.activeStory) return
    //     if (!state.stories.activeShotList) return

    //     // // it seems we should count up the length of the lists in ORDER.   but the fields dont exist yet!  maybe activeStory is a promose?
    //     // if ("parts" in state.stories.activeStory && "order" in state.stories.activeStory.parts) {
    //     //     const num_shots = state.stories.activeStory.parts.reduce((count, part) => count = count + part.order.length, 0) 
    //     // } else {
    //     //     console.log("WARNING:  shot count is FAILING!", state.stories.activeStory)
    //     // }

    //     const num_shots = state.stories.activeShotList.length   // instead we do this, which SHOULD work beacuse deleted_at is filetered out
    //     dispatch({ type: "UPDATE_NUM_SHOTS", payload: { num_shots: num_shots } });              


    // }, [dispatch, state.stories.activeShotList]);  //  state.stories.activeStory, 


    // ACTIVE TAKE LIST
    // Listen for changes to activeTakeList
    /* useEffect(() => {
        //if (!currentUser || !state.stories.activeStoryRef) return  // user dependency removed.
        if (!state.stories.activeStory) return
        if (!state.stories.activeStoryRef) return
        let unSubscribe = () => { };
        try {
            //console.log("try")
            const activeStoryRef = state.stories.activeStoryRef
            const storyRef = query(collection(activeStoryRef, 'takes'), where('deleted_at', '==', null))
            unSubscribe = onSnapshot(storyRef, querySnapshot => {
                const activeTakeList = [];
                querySnapshot.forEach(snapDoc => {
    
                    if (snapDoc.exists()) {
                        var take = snapDoc.data();
                        take.ref = snapDoc.ref;
                        activeTakeList.push(take)
                    }
                })
                dispatch({ type: "UPDATE_ACTIVE_TAKE_LIST", payload: { activeTakeList } });
            })
        }
        catch (error) {
            console.log('Error querying data for activeTakeList: ', error)
        } finally {
            return () => unSubscribe();
        }
    }, [state.stories.activeStoryRef]); */

    // ACTIVE VENEER LIST
    // Listen for changes to activeVeneerList, and update the LUT
    useEffect(() => {
        /*
        Merge Conflict
        if (!state.stories.activeStory || !state.stories.activeStoryRef) {
            if (state.stories.activeStoryRef && !state.stories.activeStory && state.stories.storyList) {
                const data = state.stories.activeStoryRef.data();
                if (state.stories.storyList.find(story => story.portal_id === data.portal_id)) {
                    const activeStory = state.stories.storyList.find(story => story.portal_id === data.portal_id)
                    dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: currentUser.uid == activeStory.creator_id } });
                    dispatch({ type: "SET_ACTIVE_STORY", payload: { activeStory: activeStory } });
                } else {
                    initializeFromPortal(data.portal_id)
                }
            }
            return
        }*/
        if (!state.stories.activeStoryRef) return
        console.log("update current refs effect")
        let unSubscribe = () => { };
        try {
            const veneerRef = collection(state.stories.activeStoryRef, 'veneer')
            unSubscribe = onSnapshot(veneerRef, async querySnapshot => {
                const activeVeneerList = [];
                if (querySnapshot.empty) { // migrate veneers
                    const createdAt = new Date(state.stories.activeStory?.created_at).getTime();
                    const nov = new Date(2024, 11, 1).getTime();
                    const mar = new Date(2025, 3, 24).getTime();
                    if (createdAt > nov && createdAt < mar) {
                        const veneersRef = collection(state.stories.activeStoryRef, 'veneers')
                        await getDocs(veneersRef, querySnapshot => {
                            if (querySnapshot.empty) return;
                            const batch = writeBatch(db);
                            querySnapshot.forEach((qDoc) => {
                                if (qDoc.exists()) {
                                    var vn = qDoc.data();
                                    if (vn.deleted_at) return;
                                    vn.ref = qDoc.ref;
                                    activeVeneerList.push(vn)
                                    const vDoc = doc(veneerRef, qDoc.id);
                                    batch.set(vDoc, vn, { merge: true });
                                }
                            })
                            // Commit the batch
                            batch.commit();
                        })
                    }
                } else {
                    querySnapshot.forEach((qDoc) => {
                        if (qDoc.exists()) {
                            var vn = qDoc.data();
                            if (vn.deleted_at) return;
                            vn.ref = qDoc.ref;
                            activeVeneerList.push(vn)
                        }
                    })
                }
                dispatch({ type: "UPDATE_ACTIVE_VENEER_LUT", payload: { activeVeneerList: activeVeneerList } });
            })
        }
        catch (error) {
            console.log('Error querying data for activeVeneerList: ', error)
        }
        // Fix 5: Proper cleanup — unsubscribe on dependency change
        return () => { unSubscribe(); };
    }, [state.stories.activeStoryRef]);

    // Current Refs
    /*useEffect(() => {
        if (!state.stories.activeStory || !state.stories.activeStoryRef) return
        // console.log("UPDATE_CURRENT_REFS effect", state.stories.activeStory, state.stories.activeStory?.currentRefs)
        // dispatch({ type: "UPDATE_CURRENT_REFS", payload: state.stories.activeStory.currentRefs || [] });
    }, [state.stories.activeStory, state.stories.activeStoryRef]);*/


    useEffect(() => {
        let prioritizeLivePreview = true
        if (!state.app.userIsStoryOwner) {
            prioritizeLivePreview = !state.app.settings.communityCanViewSavedPreview
        }
        dispatch({ type: "UPDATE_PRIORITIZE_LIVE_PREVIEW", payload: { prioritizeLivePreview: prioritizeLivePreview } });
    }, [state.app.userIsStoryOwner, state.app.settings.communityCanViewSavedPreview]);

    useEffect(() => {
        if (!state.stories.generateStory) return
        if (state.stories.generateStory.part_id) {
            const availableCredits = (state.userData?.credits || 0) + (state.stories?.activeStoryCredits || 0);
            if (state.stories.generateStory && availableCredits - state.stories.generateStory.cost < 0) return;
            const userRef = doc(db, 'users', currentUser.uid);
            const allStories = [
                ...(state.stories.storyList || []),
                ...(state.stories.sharedStoryList || [])
            ];
            const storyRef = allStories.find(story => story.portal_id === state.stories.generateStory.portal_id)?.ref || state.stories.activeStoryRef;
            generateCompose(storyRef)
        } else {
            const story = emptyStory(state.stories.generateStory.portal_id, false, true);
            dispatch({ type: "NEW_ACTIVE_STORY", payload: { newStory: story } });
        }
    }, [state.stories.generateStory]);

    useEffect(() => {
        const run = async () => {
            if (!state.app.newRefTake || !state.stories.activeStoryRef) return;
            const activeStoryRef = state.stories.activeStoryRef
            const newTake = { ...state.app.newRefTake.take, ...{ "is_ref": true, "uploaded_at": serverTimestamp(), "uploader_id": currentUser.uid, "uploader_email": currentUser?.email || null, "deleted_at": null } };
            const refList = state.stories.activeRefList || []
            if (state.app.newRefTake.addUserRef) {
                const existingRefs = state.generate.userRefs || []
                const newUserRefs = [...existingRefs, { id: newTake.id, type: "ref", active: false }]
                const { persistUserState } = await import("../helpers/index.js");
                await persistUserState({ patch: { userRefs: newUserRefs }, dispatch, currentUser, activeStoryRef });
            }
            let takeRef = doc(activeStoryRef, 'takes', newTake.id);
            await setDoc(takeRef, newTake, { merge: true })

            const activeRefList = refList.find(ref => ref.id === newTake.id) ? refList : [...refList, newTake];
            dispatch({ type: "UPDATE_ACTIVE_REF_LIST", payload: { activeRefList } });
            dispatch({ type: "CLEAR_NEW_REF_TAKE", payload: {} });
        };
        run();
    }, [state.app.newRefTake, state.stories.activeStoryRef]);

    useEffect(() => {
        const run = async () => {
            if (!state.app.updateUserState || !currentUser || !state.stories.activeStoryRef) return
            const activeStoryRef = state.stories.activeStoryRef
            // change to userRefs/uid
            const stateRef = doc(activeStoryRef, "user_state", currentUser.uid)
            try {
                await setDoc(stateRef, state.app.updateUserState, { merge: true })
            } catch (err) {
                console.error('[updateUserState] setDoc failed — in-memory state will still be updated', err);
            }
            if (state.app.updateUserState.hasOwnProperty("userRefs")) {
                dispatch({ type: "UPDATE_USER_REFS", payload: state.app.updateUserState.userRefs || [] });
            }
            if (state.app.updateUserState.hasOwnProperty("method")) {
                dispatch({ type: "UPDATE_SELECTED_METHOD", payload: state.app.updateUserState.method });
                /*if (!state.app.updateUserState.model) {
                    dispatch({ type: "UPDATE_SELECTED_MODEL", payload: null });
                }*/
            }
            if (state.app.updateUserState.hasOwnProperty("model")) {
                dispatch({ type: "UPDATE_SELECTED_MODEL", payload: state.app.updateUserState.model });
            }
            if (state.app.updateUserState.hasOwnProperty("prompt")) {
                dispatch({ type: "UPDATE_PROMPT", payload: state.app.updateUserState.prompt });
            }
            dispatch({ type: "CLEAR_USER_STATE", payload: {} });
        };
        run();
    }, [state.app.updateUserState])

    useEffect(() => {
        if (!state.app.storyImport || !Object.keys(state.app.storyImport).length) return;
        if (!currentUser) return draft;
        const importPayload = state.app.storyImport; //draft.stories.activeStory.ref //action.payload.storyRef;
        // let storyId = null;
        const bw = state.app.settings.proxyIsBlackAndWhite;
        const getStoryData = async () => {
            const portalRef = doc(db, 'portals', importPayload.portalId)
            return await getDoc(portalRef).then(async (pRef) => {
                if (pRef.exists()) {
                    //console.log("PORTAL data:", doc.data());  //defeats security if we just log it out, lol
                    const data = pRef.data();
                    //const user_id = data.creator_id
                    dispatch({ type: "UPDATE_PORTAL", payload: { ...data, id: importPayload.portalId } });
                    return { story_id: data.story_id, creator_id: data.creator_id }

                }
            });
        }

        const handleStory = async () => {
            const storyData = /*(draft.stories.activeStory?.portal_id == action.payload.portalId) ? { story_id: draft.stories.activeStory.story_id, creator_id: draft.stories.activeStory.creator_id } : */await getStoryData();
            const story_id = storyData.story_id;
            const creator_id = storyData.creator_id;
            // console.log("HANDLE STORY ", story_id, action.payload.portalId, draft.stories.activeStory?.portal_id)
            const importStoryRef = doc(db, 'users', creator_id, 'stories', story_id);
            const uploader_id = (importPayload.currentUser) ? importPayload.currentUser.uid : "anon"
            const uploader_email = (importPayload.currentUser?.email) ? importPayload.currentUser.email : (currentUser?.email || null)
            const importData = {
                video_path: importPayload.video_path,
                thumbnail: importPayload.thumbnail,
                id: importPayload.id,
                type: "storyboard",
                uploaded_at: serverTimestamp(),
                //modified_at: Date.now(), 
                uploader_id: uploader_id,
                uploader_email: uploader_email,
                proxyLUT: bw ? "gray_strong_contrast" : "none",
                deleted_at: null
            };
            const importRef = doc(importStoryRef, 'imports', nanoid()) // importPayload.id)
            setDoc(importRef, importData, { merge: true })
                .then(() => {
                    vidRef;
                    if (currentUser.uid == storyData.creator_id) dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: true } });
                    logEvent(analytics, 'NEW_AUTOSTORY')
                })
                .catch((error) => {
                    console.log('Error importing scenes:', importRef)
                })
            //console.log("ADD_TAKE_TO_SHOT  cloud ", payload.take_id)
            dispatch({ type: "STORY_IMPORT", payload: {} });
        }


        /*if(!importStoryRef && action.payload.portalId) {
            getStoryRef();
        } else {
            handleStory();
        }*/
        handleStory();
    }, [state.app.storyImport]);


    // // ACTIVE ALBUM
    // // Fetch data for activeAlbum (based on state.activeAlbumRef)
    // useEffect(() => {

    //     const setupDefaultAlbum = async () => {
    //         const albumRef = await setDoc(doc(db, 'albums').doc();
    //         const newAlbum = albumConstructor();
    //         return albumRef.set(newAlbum)
    //             .then(() => albumRef)
    //             .catch((error) => {
    //                 console.log('Error creating default album:')
    //                 return error
    //             })
    //     }

    //     const initializeActiveAlbumRef = async () => {
    //         let activeAlbumRef = ""
    //         if (state.inspiration.initialAlbumID) {
    //             activeAlbumRef = await setDoc(doc(db, 'albums').doc(state.inspiration.initialAlbumID);
    //             // Check if album exists 
    //             activeAlbumRef = await activeAlbumRef.get().then((doc) => {
    //                 if (doc.exists && !doc.get("deleted_at")) {
    //                     return activeAlbumRef
    //                 } else {
    //                     return null;
    //                 }
    //             })
    //         }

    //         // If the album does not exist, check if there are other albums to set it to and set it to the first one
    //         if (!activeAlbumRef) {
    //             // The original activeAlbum no longer exists, so check fo any other albums
    //             activeAlbumRef = await await setDoc(doc(db, 'albums')
    //                 .where("status", "==", "display")
    //                 .where("deleted_at", "==", null)
    //                 .limit(1)
    //                 .get().then((querySnapshot) => {
    //                     if (!querySnapshot.empty) {
    //                         return querySnapshot.docs[0].ref
    //                     } else {
    //                         return null
    //                     }
    //                 })
    //         }

    //         // If still no albums, create one and set it
    //         if (!activeAlbumRef) {
    //             activeAlbumRef = await setupDefaultAlbum()
    //                 .then((newAlbumRef) => newAlbumRef)
    //                 .catch((error) => {
    //                     console.log('Unable to initialize activeAlbumRef. Creating new album failed.', error)
    //                 })
    //         }

    //         if (activeAlbumRef) {
    //             dispatch({ type: "UPDATE_ACTIVE_ALBUM_REF", payload: { activeAlbumRef } });
    //         }

    //     }


    //     if (!currentUser) return
    //     if (!state.inspiration.activeAlbumRef) {
    //         try {
    //             initializeActiveAlbumRef();
    //         } catch (error) {
    //             console.log('Error initializing active album: ', error)
    //         }
    //         return
    //     }

    //     let unSubscribe = () => { };
    //     try {
    //         const activeAlbumRef = state.inspiration.activeAlbumRef
    //         unSubscribe = activeAlbumRef.onSnapshot(doc => {
    //             if (doc.exists) {
    //                 const activeAlbum = doc.data();
    //                 if (!activeAlbum.deleted_at) {
    //                     activeAlbum.ref = doc.ref;
    //                     dispatch({ type: "UPDATE_ACTIVE_ALBUM", payload: { activeAlbum } });
    //                 }
    //             }
    //         });
    //     } catch (error) {
    //         console.log('Error subscribing to active album updates: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }
    // }, [dispatch, state.inspiration.activeAlbumRef, state.inspiration.initialAlbumID, currentUser]);

    // // ACTIVE ALBUM SHOT LIST
    // // Listen for changes to activeAlbumCardList
    // useEffect(() => {
    //     if (!currentUser || !state.inspiration.activeAlbumRef) return
    //     const activeAlbumRef = state.inspiration.activeAlbumRef
    //     // const activeAlbumRef = await setDoc(doc(db, 'albums').doc(state.inspiration.activeAlbumRef.id)
    //     // if (typeof activeAlbumRef.collection !== 'function') return
    //     let unSubscribe = () => { };
    //     try {
    //         const shotsRef = activeAlbumRef
    //             .collection('cards')
    //             .where('deleted_at', '==', null)
    //             .where('display', '==', true)
    //             .orderBy('orientation', 'desc')
    //             .orderBy('created_at', 'asc')
    //         unSubscribe = shotsRef.onSnapshot(querySnapshot => {
    //             const cardList = [];
    //             querySnapshot.forEach((doc) => {
    //                 if (doc.exists) {
    //                     const card = doc.data();
    //                     card.ref = doc.ref;
    //                     cardList.push(card)
    //                 }
    //             })
    //             dispatch({ type: "UPDATE_ACTIVE_ALBUM_SHOT_LIST", payload: { activeAlbumCardList: cardList } });
    //         })
    //     }
    //     catch (error) {
    //         console.log('Error querying data for activeAlbumCardList: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }
    // }, [currentUser, dispatch, state.inspiration.activeAlbumRef]);

    // TODO: preload images for story and inspiration? 


    // Expose test helpers for E2E tests in emulator environments
    if (process.env.REACT_APP_USE_EMULATOR === 'true') {
        window.__test_dispatch = dispatch;
        window.__test_doUserInit = doUserInit;
        // __test_initStory(portalId?) — triggers the full setActive=true story-load path.
        // Replaces the old doUserInit(true) pattern now that doUserInit is user-data-only.
        window.__test_initStory = async (portalId) => {
            const pid = portalId || state.app.portal?.id;
            if (!pid) { console.warn('[TEST] __test_initStory: no portal id'); return; }
            const cached = portalCacheRef.current?.id === pid ? portalCacheRef.current : null;
            await initializeFromPortal(pid, null, true, cached);
        };
        window.__test_getState = () => state;
        // __test_isUserReady() — returns true once userData has been populated.
        // Uses the same guard the app uses (Object.keys check) rather than .id,
        // because setupNewUser() doesn't set an id field on the user doc.
        window.__test_isUserReady = () => !!(state.userData && Object.keys(state.userData).length > 0);
    }

    return (
        <AppContext.Provider value={{ state, dispatch }}>
            {children}
        </AppContext.Provider>
    );
}

export default AppContext;